// The weapon models, from James's concept sheets (/mnt/project-files/weapons/concepts/, notes in
// MODELLING_NOTES.md). Every gun is a set of rigid parts built with the robots' print kit, and a
// pose() that writes each part's matrix from what the gun is doing this frame (GunAct): slides
// blow back, the revolver's cylinder turns a chamber per shot and swings out to load, the bolt
// gun cycles after every shot, barrels break open, drums swing off, the Auto MG's barrels and fan
// spin up and its tips glow with heat, the Duel Blaster's vial fills as it charges.
//
// Frame: origin where the gun hand closes on the grip, barrel down +Z, top +Y, metres. Each
// reload's part timing matches the support-hand path of its ReloadK in render/cowboys/holds.ts
// (same phase u), so the hand arrives as the drum, mag, shell or vial does.

import * as THREE from 'three';
import { PartBuilder, Rig, T, type Num3, type SubOpts } from '../render/robots/kit';
import { DC } from '../render/robots/decals';
import { Canopy, Elem, W, WEAPONS } from '../../sim/weapons';
import {
  C, type GunAct, type Paint, type PartSink, about, ball, box, clamp01, cx, cy, cz, guard, inward, kick, lathe,
  moved, pulse, ramp, rb, ringX, ringY, ringZ, rod, side, sideDecal, slab, smooth, IDENT, Sheet,
} from './gunkit';

export type Add = (g: THREE.BufferGeometry, col: Num3, o?: SubOpts) => void;

/** collects a gun's rigid parts (one Rig slot each) */
export class GB {
  readonly rig = new Rig();
  constructor(readonly ol = 0.0022) {}
  part(fn: (add: Add) => void, shadow: 'box' | 'round' | 'none' = 'box', count = 1): number {
    const pb = new PartBuilder(this.ol);
    fn((g, col, o = {}) => { pb.add(g, col, { rust: 0.12, chip: 0.8, outline: true, jitter: 0.05, ...o }); });
    return this.rig.add(pb.build(), count, shadow);
  }
}

export interface GunModel {
  rig: Rig;
  muzzle: THREE.Vector3;
  pose(a: GunAct, s: PartSink): void;
}

const PI = Math.PI;
const M1 = new THREE.Matrix4(), M2 = new THREE.Matrix4(), M3 = new THREE.Matrix4(), V3 = new THREE.Vector3();
const NO: SubOpts = { outline: false };
const DEC = (cell: (typeof DC)[keyof typeof DC]): SubOpts => ({ decal: cell, outline: false, rust: 0, chip: 0 });
/** a point d along a part raked by rx about X from its centre (y, z) */
const rk = (y: number, z: number, rx: number, d: number): [number, number] => [y + d * Math.cos(rx), z + d * Math.sin(rx)];
const shade = (c: number, k: number) => new THREE.Color(c).multiplyScalar(k).getHex();
/** a bore: dark disc just proud of a barrel end */
const bore = (add: Add, r: number, z: number, x = 0, y = 0) => add(cz(r, r, z - 0.003, z + 0.0012, 10, x, y), C.dark, NO);
/** a row of rivet heads along a line */
function rivets(add: Add, col: number, n: number, a: [number, number, number], b: [number, number, number], r = 0.0035) {
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    add(ball(r, a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t, 6, 4), col, NO);
  }
}
/** pistol trigger blade */
const trigger = (add: Add, col: number, y = 0.022, z = 0.042) => add(box(0.006, 0.022, 0.007, 0, y, z, 0.35), col, NO);

// ================================================================== THE STARTER
// The boxy company pistol, traced from starter.png: a cream receiver, a plum upper with an orange
// top strap, a deep cream dust cover under the barrel, mint grip panels in a bronze frame, a small
// sensor eye that watches what the tracking rounds will hit. The upper blows back per shot and
// locks open through the mag change.
function starter(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(335, 345, 0.00026);
  const cream = P.enamel(C.cream), mint = P.enamel(C.mint), plum = P.enamel(C.plum), orange = P.enamel(C.burnt), bronze = P.enamel(C.bronze);
  const GR = 0.2;
  const body = g.part((add) => {
    // dust cover and frame rail
    add(S.blk(560, 300, 1035, 465, 0.028), cream);
    add(S.blk(160, 325, 560, 362, 0.029), bronze);
    add(S.slab([[560, 455], [1035, 455], [1035, 467], [565, 467]], 0.0284), shade(cream, 0.72), NO);
    // grip: bronze frame, mint panels both sides, a seam across the panel
    add(S.slab([[215, 345], [420, 350], [428, 440], [356, 744], [168, 744], [155, 700]], 0.03), bronze);
    for (const sx of [1, -1]) {
      add(S.slab([[268, 364], [412, 364], [350, 712], [196, 712]], 0.003, sx * 0.0155, 0.0008), mint);
      add(S.slab([[240, 525], [380, 520], [377, 528], [238, 533]], 0.0012, sx * 0.0172, 0.0003), shade(mint, 0.7), NO);
      add(ball(0.0026, sx * 0.0168, S.y(378), S.z(310), 6, 4), P.brass, NO);
    }
    add(guard(S.y(392), S.z(505), 0.018, 0.0026), bronze, NO);
    trigger(add, P.metal, S.y(405), S.z(470));
    // barrel stub out of the dust cover
    add(S.tube(1030, 1100, 222, 24, 12), P.brass);
    add(S.tube(1100, 1160, 222, 26, 12), C.dark);
    bore(add, 0.0035, S.z(1160), 0, S.y(222));
    // screws and a label
    for (const sx of [1, -1]) {
      add(ball(0.0022, sx * 0.0142, S.y(430), S.z(600), 6, 4), P.metal, NO);
      add(ball(0.0022, sx * 0.0142, S.y(430), S.z(1000), 6, 4), P.metal, NO);
    }
    for (const sx of [1, -1] as const) add(sideDecal(0.026, 0.008, sx, sx * 0.0142, S.y(340), S.z(720)), 0xffffff, DEC(DC.barcode));
  });
  const slide = g.part((add) => {
    // receiver block, hammer plate at the back, the plum upper and its orange strap
    add(S.blk(185, 182, 605, 325, 0.03), cream);
    add(S.slab([[155, 192], [190, 190], [190, 345], [162, 345], [150, 300]], 0.024), bronze);
    add(S.blk(605, 205, 1035, 300, 0.031), plum);
    add(S.blk(605, 180, 1035, 207, 0.029), orange);
    add(S.slab([[545, 185], [605, 185], [605, 322], [545, 322]], 0.0305), shade(cream, 0.9), NO);
    for (let i = 0; i < 7; i++) add(S.blk(200 + i * 14, 230, 206 + i * 14, 310, 0.0306, 0.0004), shade(cream, 0.7), NO);
    // ejection port, sights
    add(S.slab([[640, 230], [760, 230], [760, 255], [640, 255]], 0.0002, 0.0157, 0), C.dark, NO);
    add(S.slab([[220, 183], [322, 183], [318, 170], [290, 168], [262, 158], [240, 158]], 0.012), P.metal);
    add(S.slab([[990, 182], [1022, 182], [1020, 150], [995, 150]], 0.006), P.metal);
    for (const sx of [1, -1]) add(ball(0.0022, sx * 0.0156, S.y(285), S.z(622), 6, 4), P.metal, NO);
    for (const sx of [1, -1] as const) add(sideDecal(0.032, 0.0075, sx, sx * 0.0157, S.y(268), S.z(880)), 0xffffff, DEC(DC.barcode));
    for (const sx of [1, -1] as const) add(sideDecal(0.026, 0.01, sx, sx * 0.0152, S.y(240), S.z(300)), 0xffffff, DEC(DC.stripes));
  });
  const mag = g.part((add) => {
    add(S.slab([[230, 380], [370, 380], [325, 712], [205, 712]], 0.022), P.metal);
    add(S.slab([[180, 712], [352, 712], [347, 746], [175, 746]], 0.031), P.brass);
  });
  const eye = g.part((add) => {
    for (const sx of [1, -1]) {
      add(ringX(S.l(22), 0.0018, sx * 0.0155, S.y(228), S.z(515), 14), P.brass, NO);
      add(ball(S.l(14), sx * 0.0148, S.y(228), S.z(515), 10, 7, 0.35, 1, 1), C.tealDk, { glow: C.tealGlow, outline: false });
    }
  }, 'none');
  const mDir = rk(0, 0, GR, -1);
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(222), S.z(1162)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      const rl = a.reload > 0.4;
      const u = a.reloadU;
      const lock = rl ? pulse(u, 0.04, 0.1, 0.72, 0.77) : 0;
      const back = moved(0, 0, -0.03 * Math.max(lock, Math.min(1, kick(a.shotAge, 0.09))), M1);
      s.part(slide, back, 0);
      // mag: drops free, gone while the hand fetches one, then seats with a slap
      if (!rl || u < 0.1 || u > 0.66) s.part(mag, IDENT, 0);
      else if (u < 0.24) { const d = ramp(u, 0.1, 0.24) * 0.16; s.part(mag, moved(0, mDir[0] * d, mDir[1] * d), 0); }
      else if (u > 0.52) { const d = (1 - ramp(u, 0.56, 0.66)) * 0.09; s.part(mag, moved(0, mDir[0] * d, mDir[1] * d), 0); }
      const track = a.shotAge < 1.2 ? 1 : 0;
      s.part(eye, back, 0.45 + track * (0.8 + 0.3 * Math.sin(a.t * 18)));
    },
  };
}

// ================================================================== PULSE RIFLE
// Traced from pulse_rifle.png: a round capacitor housing at the back with three glass cells
// standing in it (they light per round of a burst), a long cream receiver with a teal stripe, a
// ribbed barrel, a scope with a boxy lens housing, a ribbed drum hanging under the middle. Reload:
// the drum swings down off its front hinge while the cells go dark and charge back one by one.
function pulseRifle(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(430, 432, 0.00078);
  const cream = P.enamel(C.cream), teal = P.enamel(C.teal), plum = P.enamel(C.plum), orange = P.enamel(C.orange), burnt = P.enamel(C.burnt);
  const AY = 325;
  const body = g.part((add) => {
    // capacitor housing: a round can, dark bands under the cells, an orange cradle
    add(S.tube(262, 368, AY, 70, 18), burnt);
    add(S.tube(566, 600, AY, 70, 18), burnt);
    add(S.tube(255, 275, AY, 74, 18), plum);
    for (const x of [360, 434, 500, 566]) add(S.tube(x, x + 8, AY, 72, 18), plum, NO);
    // the open bay the cells stand in: a dark back wall, a cradle under them
    add(S.blk(368, 262, 566, 352, 0.03, 0.001), shade(plum, 0.7), NO);
    add(S.slab([[368, 348], [566, 348], [566, 374], [536, 396], [398, 396], [368, 374]], 0.11, 0, 0.003), burnt);
    // receiver: cream box, teal stripe, dark keel, a front collar
    add(S.blk(598, 252, 1095, 348, 0.088, 0.003), cream);
    add(S.blk(598, 346, 1095, 376, 0.088, 0.002), teal);
    add(S.blk(598, 374, 1095, 402, 0.08, 0.002), plum);
    for (const x of [700, 800, 900, 1000]) add(S.blk(x, 254, x + 4, 400, 0.0884, 0.0005), shade(cream, 0.72), NO);
    add(S.tube(1090, 1122, 337, 40, 16), plum);
    // ribbed barrel
    add(S.tube(1120, 1290, 337, 15, 12), P.metal);
    for (let i = 0; i < 9; i++) add(S.tube(1132 + i * 16, 1140 + i * 16, 337, 20, 12), shade(P.metal, 0.8), NO);
    add(S.tube(1270, 1292, 337, 21, 12), P.metal);
    bore(add, 0.008, S.z(1292), 0, S.y(337));
    // underside hardware and the front sight
    add(S.blk(900, 400, 1090, 412, 0.03), P.metal, NO);
    add(S.slab([[1068, 252], [1080, 252], [1080, 215], [1074, 215]], 0.006), P.metal);
    // scope: tube, mounts, boxy lens housing with a lens front and back
    add(S.tube(560, 720, 175, 26, 14), cream);
    add(S.tube(552, 568, 175, 29, 14), plum);
    for (const x of [600, 660]) add(S.tube(x, x + 6, 175, 27, 14), P.metal, NO);
    add(S.blk(705, 132, 850, 220, 0.056, 0.003), orange);
    add(S.blk(740, 132, 850, 146, 0.057, 0.001), plum, NO);
    add(S.tube(848, 854, 178, 34, 14), C.glass, NO);
    add(S.slab([[600, 198], [740, 198], [740, 252], [600, 252]], 0.022), P.metal);
    for (const x of [622, 708]) add(S.blk(x, 196, x + 14, 252, 0.03, 0.001), P.metal);
    add(S.drum(718, 225, 16, 0.026, 12), plum);
    add(S.slab([[598, 150], [612, 150], [612, 135], [598, 135]], 0.01), P.metal);
    // stock: cream with the teal underside, a butt plate
    add(S.slab([[62, 412], [345, 418], [395, 446], [402, 482], [302, 566], [272, 542], [232, 532], [62, 532]], 0.05, 0, 0.0025), cream);
    add(S.slab([[62, 488], [235, 492], [272, 542], [232, 532], [62, 532]], 0.0506, 0, 0.0015), teal, NO);
    add(S.blk(52, 410, 74, 536, 0.054, 0.002), plum);
    add(S.blk(345, 395, 405, 440, 0.05, 0.002), plum);
    // pistol grip and guard
    add(S.slab([[405, 425], [462, 428], [450, 560], [392, 556]], 0.034, 0, 0.0025), plum);
    for (let i = 0; i < 4; i++) add(S.slab([[402 - i * 3, 460 + i * 25], [455 - i * 3, 462 + i * 25], [454 - i * 3, 468 + i * 25], [401 - i * 3, 466 + i * 25]], 0.0346, 0, 0.0004), shade(plum, 0.7), NO);
    add(guard(S.y(412), S.z(492), 0.019, 0.003), P.metal, NO);
    trigger(add, P.metal, S.y(440), S.z(480));
    for (const sx of [1, -1] as const) add(sideDecal(0.07, 0.016, sx, sx * 0.0445, S.y(300), S.z(820)), 0xffffff, DEC(DC.stripes));
    for (const sx of [1, -1] as const) add(sideDecal(0.03, 0.03, sx, sx * 0.0445, S.y(300), S.z(960)), 0xffffff, DEC(DC.plate));
  });
  // a cell: glass can standing in the housing, a brass coil cap
  const cells = g.part((add) => {
    add(cy(S.l(32), S.l(14), 14, 0, S.y(352), 0), P.metal, NO);
    add(cy(S.l(29), S.l(92), 16, 0, S.y(300), 0), C.cream, { glow: C.orangeGlow, rust: 0, chip: 0.1 });
    for (const y of [272, 300, 328]) add(ringY(S.l(29.5), 0.0016, 0, S.y(y), 0, 16), shade(C.cream, 0.8), NO);
    add(cy(S.l(31), S.l(10), 14, 0, S.y(250), 0), P.brass, NO);
    add(ringY(S.l(14), 0.0024, 0, S.y(238), 0, 10), P.brass, NO);
    add(cy(S.l(6), S.l(16), 8, 0, S.y(234), 0), P.brass, NO);
  }, 'round', 3);
  // the drum: a ribbed can hanging under the receiver, axis up
  const drum = g.part((add) => {
    add(S.blk(530, 398, 625, 412, 0.05), P.metal);
    const R = S.l(84), H = S.l(160), y = S.y(488), z = S.z(575);
    add(T(new THREE.CylinderGeometry(R, R, H, 20), 0, y, z, 0, 0, 0, 0.62, 1, 1), burnt);
    for (let i = 0; i < 14; i++) { const an = (i / 14) * PI * 2; add(box(0.004, H * 0.92, 0.004, Math.cos(an) * R * 0.63, y, z + Math.sin(an) * R * 1.01), shade(burnt, 0.6), NO); }
    for (const yy of [y + H / 2, y - H / 2]) add(T(new THREE.CylinderGeometry(R * 1.02, R * 1.02, 0.008, 20), 0, yy, z, 0, 0, 0, 0.62, 1, 1), plum, NO);
    add(S.drum(668, 492, 7, 0.02, 8), P.metal);
  }, 'round');
  const cellZ = [S.z(400), S.z(468), S.z(535)];
  const HY = S.y(405), HZ = S.z(655);
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(337), S.z(1293)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      const rl = a.reload > 0.4, u = a.reloadU;
      for (let i = 0; i < 3; i++) {
        let gl = 0.55;
        if (a.burst > i && a.shotAge < 0.45) gl += 1.6 * (1 - a.shotAge / 0.45);
        if (rl) gl = u < 0.08 ? gl : u < 0.7 + i * 0.08 ? 0.04 : 0.55 + 1.4 * (1 - ramp(u, 0.7 + i * 0.08, 0.86 + i * 0.05));
        s.part(cells + i, moved(0, 0, cellZ[i]), gl);
      }
      // drum: hinge down off the front of the well, drop, then a fresh one swings up and clicks
      if (!rl) { s.part(drum, IDENT, 0); return; }
      let ang = 0, dy = 0, show = true;
      if (u < 0.28) ang = -0.9 * ramp(u, 0.08, 0.2);
      else if (u < 0.36) { ang = -0.9; dy = -0.12 * ramp(u, 0.28, 0.36); }
      else if (u < 0.52) show = false;
      else { ang = -0.9 * (1 - ramp(u, 0.58, 0.68)); dy = -0.06 * (1 - ramp(u, 0.52, 0.58)); }
      if (show) s.part(drum, about(0, HY, HZ, 1, 0, 0, ang, 0, dy, 0), 0);
    },
  };
}

// ================================================================== AUTO MG
// Traced from auto_mg.png (flipped to fire right): a stand-mixer head at the back with its carry
// handle, a big cream barrel housing with an orange band, a fluted cowl, the six-barrel cluster
// whose tips glow with heat as it spins up, an orange fan motor on the flank that spins with it,
// the finned feed box in the middle. The mounting stand became a grip and a front handle. Reload:
// the feed hatch flips up, the box drops out and a fresh one goes in, hatch down, crank it.
function autoMG(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(495, 398, 0.00085);
  const cream = P.enamel(C.cream), orange = P.enamel(C.orange), plum = P.enamel(C.plum);
  const AY = 330, FX = 0.06, FY = S.y(458), FZ = S.z(310);
  const body = g.part((add) => {
    // mixer head: rounded cream housing, orange band, a rear knob
    add(S.slab([[236, 184], [578, 182], [592, 196], [592, 300], [560, 346], [300, 346], [250, 332], [222, 300], [214, 236], [222, 200]], 0.12, 0, 0.005), cream);
    add(S.blk(318, 182, 350, 346, 0.1215, 0.002), orange, NO);
    add(S.blk(282, 284, 545, 300, 0.1215, 0.001), shade(cream, 0.72), NO);
    add(S.tube(110, 222, 325, 15, 10), P.metal);
    add(S.tube(110, 128, 325, 19, 10), P.metal);
    // carry handle
    add(rod(S.p(262, 186), S.p(276, 112), 0.006), P.steel);
    add(rod(S.p(585, 190), S.p(575, 112), 0.006), P.steel);
    add(rod(S.p(268, 112), S.p(580, 112), 0.006), P.steel);
    add(S.tube(322, 515, 111, 12, 10), C.cord);
    // barrel housing, cowl
    add(S.tube(700, 1168, AY, 146, 24), cream);
    add(S.tube(1118, 1142, AY, 147, 24), orange, NO);
    add(S.tube(800, 806, AY, 147, 24), shade(cream, 0.75), NO);
    add(S.tube(1160, 1296, AY, 150, 24, 0, 142), shade(cream, 0.92));
    for (let i = 0; i < 24; i++) { const an = (i / 24) * PI * 2; add(box(0.008, 0.016, S.l(100), Math.cos(an) * S.l(150), S.y(AY) + Math.sin(an) * S.l(150), S.z(1235), 0, 0, an), C.dark, NO); }
    add(S.tube(1290, 1300, AY, 128, 20), shade(P.metal, 0.6), NO);
    // middle: orange motor block over the feed bay, the cream front frame
    add(S.blk(565, 250, 668, 346, 0.1, 0.004), orange);
    add(S.drum(585, 290, 12, 0.104, 10), P.metal);
    add(S.blk(665, 290, 805, 500, 0.105, 0.003), cream);
    add(S.blk(560, 498, 668, 520, 0.09, 0.002), P.metal);
    for (const sx of [1, -1] as const) add(sideDecal(0.05, 0.03, sx, sx * 0.0527, S.y(330), S.z(760)), 0xffffff, DEC(DC.plate));
    for (const sx of [1, -1] as const) add(sideDecal(0.07, 0.03, sx, sx * S.l(147), S.y(410), S.z(960)), 0xffffff, DEC(DC.warn));
    // trigger frame, grip, front handle
    add(S.blk(400, 345, 562, 420, 0.07, 0.003), orange);
    add(S.slab([[470, 402], [530, 404], [516, 548], [452, 544]], 0.036, 0, 0.002), plum);
    for (let i = 0; i < 4; i++) add(S.slab([[466 - i * 3.5, 435 + i * 28], [527 - i * 3.5, 437 + i * 28], [526 - i * 3.5, 443 + i * 28], [465 - i * 3.5, 441 + i * 28]], 0.0366, 0, 0.0004), shade(plum, 0.7), NO);
    add(guard(S.y(418), S.z(560), 0.02, 0.0034), P.metal, NO);
    trigger(add, P.metal, S.y(432), S.z(548));
    add(S.blk(830, 470, 880, 495, 0.04, 0.003), P.metal);
    add(S.slab([[836, 492], [878, 492], [872, 600], [838, 600]], 0.034, 0, 0.002), plum);
    // fan motor housing on the near flank
    add(S.drum(310, 458, 78, 0.05, 24, FX - 0.004), orange);
    add(ringX(S.l(74), 0.005, FX + 0.022, FY, FZ, 24), P.metal, NO);
    for (let i = 0; i < 8; i++) { const an = (i / 8) * PI * 2; add(box(0.004, 0.003, S.l(60), FX + 0.024, FY + Math.sin(an) * S.l(44), FZ + Math.cos(an) * S.l(44), -an + PI / 2), P.steel, NO); }
    add(S.drum(310, 458, 66, 0.003, 22, FX + 0.0225), C.dark, NO);
    add(S.slab([[390, 378], [500, 378], [500, 470], [385, 450]], 0.05, FX - 0.004), orange);
    add(S.blk(418, 378, 498, 448, 0.002, 0.0005, FX + 0.0215), cream, NO);
  });
  const barrels = g.part((add) => {
    for (let i = 0; i < 6; i++) {
      const an = (i / 6) * PI * 2, x = Math.cos(an) * S.l(48), y = S.y(AY) + Math.sin(an) * S.l(48);
      add(cz(S.l(18), S.l(18), S.z(1290), S.z(1352), 10, x, y), P.steel);
      add(cz(S.l(19), S.l(19), S.z(1352), S.z(1415), 10, x, y), shade(P.metal, 0.8), { glow: C.orangeGlow });
      bore(add, S.l(10), S.z(1415), x, y);
    }
    add(cz(S.l(30), S.l(30), S.z(1290), S.z(1400), 10, 0, S.y(AY)), P.metal, NO);
    add(cz(S.l(70), S.l(70), S.z(1340), S.z(1350), 16, 0, S.y(AY)), P.metal, NO);
  }, 'round');
  const fan = g.part((add) => {
    for (let i = 0; i < 7; i++) { const an = (i / 7) * PI * 2; add(box(0.003, S.l(56), S.l(20), FX + 0.026, FY + Math.sin(an) * S.l(30), FZ + Math.cos(an) * S.l(30), -an), C.ink, NO); }
    add(S.drum(310, 458, 18, 0.012, 12, FX + 0.028), P.steel, NO);
  }, 'none');
  // the finned feed box in the bay
  const drum = g.part((add) => {
    add(S.blk(560, 345, 666, 498, 0.088, 0.003), P.metal);
    for (let i = 0; i < 12; i++) add(S.blk(556, 352 + i * 12, 670, 358 + i * 12, 0.094, 0.0008), shade(P.metal, 0.6), NO);
    for (const x of [590, 636]) add(S.blk(x - 3, 345, x + 3, 498, 0.096, 0.0006), P.steel, NO);
  }, 'box');
  const cover = g.part((add) => {
    add(S.blk(560, 232, 672, 252, 0.104, 0.003), P.metal);
    add(S.blk(640, 222, 668, 232, 0.04, 0.001), P.brass);
  });
  const crank = g.part((add) => {
    add(rod([0.055, S.y(400), S.z(735)], [0.09, S.y(400), S.z(735)], 0.005), P.steel);
    add(ball(0.011, 0.094, S.y(400), S.z(735), 8, 6), P.brass);
  }, 'none');
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(AY), S.z(1416)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      const ang = a.turn * PI * 2;
      s.part(barrels, about(0, S.y(AY), 0, 0, 0, 1, ang), a.spin * a.spin * 1.6);
      s.part(fan, about(FX, FY, FZ, 1, 0, 0, ang * 1.7 + a.t * 2), 0);
      const rl = a.reload > 0.4, u = a.reloadU;
      s.part(cover, rl ? about(0, S.y(242), S.z(560), 1, 0, 0, -1.1 * pulse(u, 0.1, 0.2, 0.72, 0.8)) : IDENT, 0);
      if (!rl) s.part(drum, IDENT, 0);
      else if (u < 0.24) s.part(drum, IDENT, 0);
      else if (u < 0.38) { const k = ramp(u, 0.24, 0.38); s.part(drum, moved(-0.06 * k, -0.12 * k * k, 0), 0); }
      else if (u > 0.54) { const k = 1 - ramp(u, 0.56, 0.68); s.part(drum, moved(-0.06 * k, -0.06 * k, 0), 0); }
      s.part(crank, moved(0, 0, rl ? -0.07 * pulse(u, 0.83, 0.88, 0.9, 0.95) : 0), 0);
    },
  };
}

// ================================================================== BOLT-ACTION SNIPER
// Traced from bolt_sniper.png, in walnut and brass instead of its red: a long hunting stock, a
// slim barrel with two bands and a hooded front sight, the long brass scope with its turret and the
// chevron, and a retro-futuristic touch in the finned brass collar and the charge vial along the
// receiver (it fills as the aim steadies). The bolt works after every shot.
function boltSniper(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(470, 432, 0.00098);
  const wood = P.wood, woodDk = shade(P.wood, 0.72), orange = P.enamel(C.orange), BY = S.y(353);
  const body = g.part((add) => {
    add(S.slab([[64, 432], [200, 416], [335, 400], [382, 412], [470, 380], [560, 366], [900, 350], [1150, 334], [1156, 388], [900, 398], [670, 418],
      [560, 442], [470, 442], [420, 454], [362, 496], [80, 585], [64, 572]], 0.046, 0, 0.0035), wood);
    add(S.slab([[52, 430], [66, 430], [82, 588], [68, 590]], 0.049, 0, 0.0015), P.brass);
    add(S.slab([[1120, 336], [1156, 334], [1156, 388], [1120, 391]], 0.0472, 0, 0.0012), woodDk, NO);
    for (const sx of [1, -1] as const) {
      add(S.slab([[282, 438], [296, 436], [298, 470], [284, 472]], 0.002, sx * 0.0236, 0.0005), P.brass, NO);
      add(ball(0.003, sx * 0.0236, S.y(452), S.z(80), 6, 4), P.brass, NO);
    }
    // receiver, floorplate, guard
    add(S.tube(480, 925, 353, 14, 14), P.metal);
    add(S.blk(560, 340, 700, 372, 0.03, 0.002), P.metal);
    add(S.blk(560, 418, 672, 446, 0.03, 0.002), P.metal);
    add(guard(S.y(442), S.z(512), 0.026, 0.0035), P.brass, NO);
    trigger(add, P.metal, S.y(455), S.z(500));
    // finned brass collar, barrel, bands, front sight
    for (let i = 0; i < 5; i++) add(S.tube(925 + i * 14, 933 + i * 14, 353, 22 - i, 14), P.brass);
    add(S.tube(925, 1475, 352, 10, 10, 0, 9), P.steel);
    add(S.tube(1150, 1172, 352, 24, 14), P.brass);
    add(S.tube(1280, 1330, 353, 18, 12), P.metal);
    add(S.slab([[1442, 342], [1470, 342], [1470, 318], [1442, 318]], 0.012), P.metal);
    add(S.tube(1440, 1476, 350, 13, 10), P.metal);
    bore(add, 0.006, S.z(1476), 0, S.y(350));
    add(S.blk(1335, 370, 1385, 378, 0.008), P.metal, NO);
    // scope: rear eyepiece, long tube, a turret, the objective bell, the chevron, mounts
    add(S.tube(186, 240, 292, 23, 16), P.brass);
    add(S.tube(240, 600, 292, 19, 16), P.brass);
    add(S.tube(600, 650, 290, 19, 16, 0, 25), P.brass);
    add(S.tube(650, 952, 288, 25, 16), P.brass);
    add(S.tube(948, 954, 288, 22, 16), C.glass, NO);
    add(S.tube(184, 188, 292, 20, 16), C.glass, NO);
    for (const x of [300, 520, 700]) add(S.tube(x, x + 8, 290, 26, 16), shade(P.brass, 0.75), NO);
    add(S.drum(420, 293, 20, 0.05, 14), P.metal);
    add(S.drum(498, 280, 14, 0.02, 12), P.metal);
    add(S.slab([[400, 312], [565, 312], [565, 340], [400, 330]], 0.016), P.metal);
    for (const sx of [1, -1]) {
      add(S.slab([[848, 278], [880, 288], [848, 298], [856, 288]], 0.002, sx * S.l(25.5), 0.0003), orange, NO);
      add(S.slab([[868, 278], [900, 288], [868, 298], [876, 288]], 0.002, sx * S.l(25.5), 0.0003), orange, NO);
    }
    // vial cradle on the left of the receiver
    add(S.blk(600, 362, 760, 378, 0.012, 0.002, -0.022), P.metal);
    for (const x of [606, 754]) add(cz(0.011, 0.011, S.z(x) - 0.003, S.z(x) + 0.003, 10, -0.03, BY), P.brass, NO);
  });
  const vial = g.part((add) => {
    add(cz(0.0085, 0.0085, S.z(608), S.z(752), 10, -0.03, BY), C.tealDk, { glow: C.tealGlow, outline: false });
  }, 'none');
  const bolt = g.part((add) => {
    add(cz(0.0125, 0.0125, S.z(476), S.z(600), 10, 0.004, BY + 0.004), P.steel);
    add(rod([0.01, BY, S.z(522)], [0.046, S.y(402), S.z(518)], 0.0045), P.steel);
    add(ball(0.0105, 0.05, S.y(406), S.z(518), 8, 6), P.brass);
    add(cz(0.009, 0.006, S.z(455), S.z(476), 8, 0.004, BY + 0.004), P.metal);
  });
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(350), S.z(1477)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      s.part(vial, IDENT, 0.2 + a.ads * 1.3 + (a.ads > 0.95 ? 0.25 * Math.sin(a.t * 9) : 0));
      // after a shot: up, back, forward, down. Through a reload it stays open for the rounds
      const ag = a.shotAge;
      let up = ramp(ag, 0.24, 0.34) * (1 - ramp(ag, 0.66, 0.76));
      let back = ramp(ag, 0.34, 0.46) * (1 - ramp(ag, 0.52, 0.64));
      if (a.reload > 0.05) { const k = smooth(a.reload * 1.6); up = Math.max(up, k); back = Math.max(back, k); }
      M1.copy(about(0, BY, 0, 0, 0, 1, 1.15 * up));
      s.part(bolt, M2.makeTranslation(0, 0, -0.085 * back).multiply(M1), 0);
    },
  };
}

// ================================================================== THE GENTLEMEN
// Traced from gentlemen.png: a lacquered stock with a sharp comb, the engraved brass receiver
// with its oval filigree panel, long side-by-side barrels with copper bands instead of red, the
// forend under them, and the spread ring floating above (it tightens on aim). Breaks open: the
// spent shells flip out, fresh ones go in.
function gentlemen(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(430, 470, 0.00108);
  const lacq = P.enamel(0x6e3c44), copper = P.enamel(C.copper), PZ = S.z(612), PY = S.y(442), BYp = 372;
  const body = g.part((add) => {
    add(S.slab([[108, 512], [300, 442], [410, 410], [458, 392], [470, 470], [426, 488], [384, 548], [352, 580], [312, 572], [300, 556], [145, 676]], 0.046, 0, 0.0035), lacq);
    add(S.slab([[100, 515], [110, 510], [148, 676], [138, 680]], 0.048, 0, 0.0012), P.metal);
    // receiver: brass, an oval engraved panel each side, a top lever
    add(S.slab([[455, 395], [540, 352], [612, 350], [612, 440], [712, 440], [712, 470], [470, 470]], 0.05, 0, 0.0025), P.brass);
    for (const sx of [1, -1]) {
      add(T(new THREE.CircleGeometry(1, 28), sx * 0.0252, S.y(420), S.z(578), 0, sx > 0 ? PI / 2 : -PI / 2, 0, S.l(118), S.l(42), 1), shade(P.brass, 0.85), NO);
      for (const [px, py, r] of [[540, 410, 14], [578, 430, 18], [620, 412, 12], [500, 425, 10], [650, 432, 9]] as const)
        add(ringX(S.l(r), 0.0011, sx * 0.0256, S.y(py), S.z(px), 12, PI * 1.5), shade(P.brass, 0.55), NO);
    }
    add(S.slab([[470, 368], [520, 352], [524, 360], [478, 374]], 0.012), P.metal);
    add(S.drum(488, 366, 10, 0.014, 10), P.metal);
    add(guard(S.y(474), S.z(520), 0.028, 0.0034), P.brass, NO);
    trigger(add, P.metal, S.y(488), S.z(505)); trigger(add, P.metal, S.y(488), S.z(530));
  });
  const barrels = g.part((add) => {
    for (const sx of [1, -1]) {
      add(S.tube(612, 1240, BYp, 25, 16, sx * 0.0262), P.metal);
      bore(add, S.l(17), S.z(1240), sx * 0.0262, S.y(BYp));
    }
    add(S.blk(612, 334, 1240, 348, 0.012, 0.001), P.metal, NO);
    add(S.blk(612, 396, 1240, 410, 0.03, 0.001), P.metal, NO);
    add(ball(0.004, 0, S.y(332), S.z(1228), 6, 4), P.brass, NO);
    for (const [x0, x1] of [[612, 628], [770, 782], [866, 876], [898, 908], [1158, 1170]]) add(S.blk(x0, 336, x1, 410, 0.107, 0.002), copper);
    add(S.slab([[700, 408], [1000, 408], [1010, 430], [990, 440], [910, 452], [720, 470], [712, 440]], 0.05, 0, 0.003), lacq);
    add(S.blk(612, 340, 640, 440, 0.054, 0.002), P.brass);
  }, 'box');
  const shells = g.part((add) => {
    add(cz(0.024, 0.024, PZ - 0.004, PZ + 0.008, 12, 0, S.y(BYp)), P.brass);
    add(cz(0.022, 0.022, PZ + 0.008, PZ + 0.07, 12, 0, S.y(BYp)), P.enamel(C.burnt));
  }, 'none', 2);
  const holo = g.part((add) => {
    add(ringZ(0.05, 0.0024, 0, 0, 0, 40), C.tealGlow, { glow: C.tealGlow, outline: false, rust: 0, chip: 0 });
    for (let i = 0; i < 4; i++) { const an = (i / 4) * PI * 2 + PI / 4; add(box(0.012, 0.003, 0.002, Math.cos(an) * 0.058, Math.sin(an) * 0.058, 0, 0, 0, an), C.tealGlow, { glow: C.tealGlow, outline: false, rust: 0, chip: 0 }); }
  }, 'none');
  const HY = S.y(285), HZ = S.z(580);
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(BYp), S.z(1241)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      const rl = a.reload > 0.3, u = a.reloadU, first = a.reloadN === 0;
      const open = rl ? Math.min(smooth(a.reload * 1.8), first ? ramp(u, 0.02, 0.12) : 1) * (a.reload > 0.85 ? 1 : smooth((a.reload - 0.3) / 0.55)) : 0;
      const hinge = about(0, PY, PZ, 1, 0, 0, 0.55 * open, 0, 0, 0, M1);
      s.part(barrels, hinge, 0);
      for (let i = 0; i < 2; i++) {
        const sx = i ? -0.0262 : 0.0262;
        if (!rl) { s.part(shells + i, M2.copy(hinge).multiply(M3.makeTranslation(sx, 0, 0)), 0); continue; }
        // first cycle: both spent shells flip out; every cycle a fresh one goes into barrel (n % 2)
        if (first && u < 0.3) {
          const k = ramp(u, 0.12, 0.3);
          M3.makeTranslation(sx * (1 + k * 2), 0.09 * k - 0.1 * k * k, -0.16 * k);
          if (k < 0.98) s.part(shells + i, M2.copy(hinge).multiply(M3), 0);
        } else if (a.reloadN % 2 === i ? u > 0.55 : a.reloadN > i || a.reloadN >= 2) {
          const k = a.reloadN % 2 === i ? 1 - ramp(u, 0.55, 0.72) : 0;
          s.part(shells + i, M2.copy(hinge).multiply(M3.makeTranslation(sx, 0.02 * k, -0.07 * k)), 0);
        }
      }
      const fade = 1 - smooth(a.reload * 2);
      if (fade > 0.02) {
        const sc = (1 - 0.35 * a.ads) * fade;
        M3.makeRotationX(-0.45).multiply(M2.makeRotationZ(a.t * 0.6)).scale(V3.set(sc, sc, sc)).setPosition(0, HY + Math.sin(a.t * 2.1) * 0.006, HZ);
        s.part(holo, M3, 0.75 + 0.15 * Math.sin(a.t * 7.3));
      }
    },
  };
}

// ================================================================== BIG IRON
// Traced from big_iron.png (flipped to fire right): a long slab barrel with an ivory top strap
// and a plum underlug, the ejector rod under it, a heavy frame round a big cylinder that shows its
// brass rounds (they empty as you fire), a fanning hammer, the yellowed ivory grip. The cylinder
// turns a chamber per shot, the hammer cocks and drops, and the cylinder swings out on the crane
// and takes a round per cycle.
function bigIron(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(290, 480, 0.00042);
  const ivory = P.enamel(C.ivory), plum = P.enamel(C.plum);
  const CY = S.y(366), CZ0 = S.z(402), CZ1 = S.z(608), CR = S.l(84);
  const body = g.part((add) => {
    // barrel: grey slab, ivory top strap, plum underlug, sights
    add(S.blk(700, 275, 1250, 382, 0.034, 0.002), P.steel);
    add(S.blk(620, 274, 1250, 298, 0.0345, 0.0015), ivory);
    add(S.slab([[856, 380], [1250, 380], [1250, 426], [872, 426], [852, 404]], 0.03, 0, 0.002), plum);
    add(S.slab([[1200, 276], [1246, 276], [1246, 258], [1204, 258]], 0.008), P.metal);
    add(S.tube(650, 935, 442, 10, 10), P.metal);
    add(S.tube(900, 935, 442, 13, 10), P.metal);
    bore(add, S.l(26), S.z(1251), 0, S.y(330));
    // frame round the cylinder window, the recoil shield behind it
    add(S.slab([[332, 270], [730, 266], [732, 284], [700, 292], [702, 462], [560, 482], [480, 492], [380, 494], [290, 472], [262, 420], [272, 320]], 0.026, 0, 0.002), P.metal);
    add(S.slab([[330, 300], [400, 282], [400, 452], [330, 440], [318, 370]], 0.04, 0, 0.002), P.metal);
    add(S.blk(608, 290, 640, 450, 0.036, 0.002), P.metal);
    add(S.blk(360, 252, 720, 264, 0.012, 0.001), P.metal, NO);
    for (const sx of [1, -1]) for (const [px, py] of [[300, 455], [412, 474], [440, 420], [680, 330]] as const) add(ball(0.0026, sx * 0.0135, S.y(py), S.z(px), 6, 4), P.steel, NO);
    for (const sx of [1, -1] as const) add(sideDecal(0.03, 0.022, sx, sx * 0.0172, S.y(330), S.z(780)), 0xffffff, DEC(DC.patch));
    // ivory grip, brass butt, escutcheon pin
    add(S.slab([[248, 452], [292, 476], [302, 520], [264, 592], [262, 716], [94, 716], [118, 636], [168, 534], [214, 472]], 0.034, 0, 0.004), ivory);
    add(S.blk(90, 714, 266, 726, 0.035, 0.002), P.brass);
    for (const sx of [1, -1]) add(ball(0.0034, sx * 0.0172, S.y(588), S.z(192), 8, 5), plum, NO);
    add(guard(S.y(512), S.z(412), S.l(54), 0.0028), P.metal, NO);
    trigger(add, P.metal, S.y(528), S.z(384));
  });
  const cyl = g.part((add) => {
    add(cz(CR, CR, CZ0, CZ1, 20, 0, 0), P.steel);
    add(cz(CR * 0.9, CR * 0.9, CZ0 - 0.002, CZ1 + 0.002, 20, 0, 0), P.metal, NO);
    for (let i = 0; i < 6; i++) { const an = (i / 6) * PI * 2; add(box(0.009, 0.006, (CZ1 - CZ0) * 0.7, Math.cos(an) * CR * 0.95, Math.sin(an) * CR * 0.95, (CZ0 + CZ1) / 2 + 0.006, 0, 0, an + PI / 2), shade(P.steel, 0.5), NO); }
    for (let i = 0; i < 6; i++) {
      const an = (i / 6) * PI * 2 + PI / 6;
      add(cz(0.0078, 0.0078, CZ1 - 0.001, CZ1 + 0.003, 8, Math.cos(an) * CR * 0.6, Math.sin(an) * CR * 0.6), C.dark, NO);
    }
    add(cz(0.007, 0.007, CZ0 - 0.01, CZ1 + 0.01, 8, 0, 0), P.metal, NO);
  }, 'round');
  const rounds = g.part((add) => {
    add(cz(0.0078, 0.0078, CZ0 - 0.004, CZ0 + 0.002, 8, CR * 0.6, 0), P.brass, NO);
    for (let k = 0; k < 4; k++) add(cz(0.0098, 0.0098, CZ0 + 0.004 + k * 0.0075, CZ0 + 0.0095 + k * 0.0075, 10, CR * 0.86, 0), P.brass, NO);
    add(cz(0.0035, 0.0035, CZ0 - 0.0045, CZ0 - 0.004, 6, CR * 0.6, 0), P.enamel(C.burnt), NO);
  }, 'none', 6);
  const hammer = g.part((add) => {
    add(S.slab([[228, 258], [300, 254], [332, 270], [300, 290], [284, 330], [272, 396], [258, 372], [266, 300], [242, 268]], 0.014, 0, 0.0015), P.metal);
    for (let i = 0; i < 3; i++) add(S.blk(240 + i * 16, 252, 246 + i * 16, 258, 0.0145, 0.0004), C.dark, NO);
    add(T(new THREE.TorusGeometry(S.l(40), 0.0026, 4, 14, PI * 0.9), 0, S.y(330), S.z(262), 0, PI / 2, PI * 0.55), P.metal);
    add(S.drum(300, 400, 16, 0.016, 10), shade(P.metal, 0.8), NO);
  }, 'none');
  const HY = S.y(398), HZ = S.z(282);
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(330), S.z(1252)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      const step = a.shots > 0 ? a.shots - 1 + ramp(a.shotAge, 0.03, 0.12) : 0;
      const rot = -step * (PI / 3);
      const out = smooth(clamp01((a.reload - 0.15) / 0.6));
      // crane: out to the left and down a touch, the cylinder still turning on its axis
      M1.makeRotationZ(rot).setPosition(-0.05 * out, CY - 0.016 * out, 0);
      s.part(cyl, M1, 0);
      let n = Math.min(6, Math.max(0, a.loaded));
      if (a.reload > 0.5 && a.reloadU > 0.62) n = Math.min(6, n + 1);
      for (let i = 0; i < 6; i++) {
        if (i >= n) continue;
        // round i sits in chamber (i + shots) so the empties stay where they were fired
        const an = ((i + a.shots) / 6) * PI * 2 + PI / 6;
        s.part(rounds + i, M2.multiplyMatrices(M1, M3.makeRotationZ(an)), 0);
      }
      const cocked = a.shotAge < 0.04 ? 0 : ramp(a.shotAge, 0.05, 0.2);
      s.part(hammer, about(0, HY, HZ, 1, 0, 0, -0.5 * cocked * (1 - out)), 0);
    },
  };
}

// ================================================================== DUEL BLASTER
// Traced from duel_blaster.png, in cream and teal instead of its red (no rear stub): a cream top,
// a teal lower body, the glass plasma chamber in its window (it fills as you charge), a bronze
// collar, a long cream barrel ending in the finned emitter (it lights at full charge), the curved
// ivory grip. Reload: the chamber lid flips, the spent vial pops, a fresh one drops in, lid shut.
function duelBlaster(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(232, 470, 0.00042);
  const cream = P.enamel(C.cream), teal = P.enamel(C.teal), plum = P.enamel(C.plum), ivory = P.enamel(C.ivory), VY = S.y(382);
  const body = g.part((add) => {
    // frame: teal lower body, cream top either side of the chamber window
    add(S.slab([[220, 345], [750, 345], [750, 460], [520, 462], [500, 500], [400, 500], [380, 470], [178, 470], [176, 425], [220, 418]], 0.036, 0, 0.002), teal);
    add(S.blk(240, 292, 318, 346, 0.036, 0.002), cream);
    add(S.blk(440, 300, 750, 346, 0.036, 0.002), cream);
    add(S.blk(306, 312, 318, 446, 0.04, 0.0015), plum);
    add(S.blk(438, 312, 450, 446, 0.04, 0.0015), plum);
    add(S.blk(306, 440, 450, 452, 0.04, 0.0015), plum);
    add(S.blk(440, 330, 470, 360, 0.04, 0.001), plum);
    add(S.slab([[238, 292], [252, 292], [250, 262], [244, 262]], 0.006), P.metal);
    for (const sx of [1, -1]) for (const [px, py] of [[212, 462], [290, 470], [470, 410]] as const) add(ball(0.0024, sx * 0.0182, S.y(py), S.z(px), 6, 4), P.metal, NO);
    for (const sx of [1, -1] as const) add(sideDecal(0.028, 0.02, sx, sx * 0.0182, S.y(405), S.z(600)), 0xffffff, DEC(DC.stripes));
    for (const sx of [1, -1] as const) add(sideDecal(0.016, 0.016, sx, sx * 0.0182, S.y(425), S.z(530)), 0xffffff, DEC(DC.bolt));
    // collar, barrel, the lug under it
    add(S.blk(750, 315, 872, 410, 0.034, 0.002), plum);
    add(S.blk(760, 318, 800, 405, 0.035, 0.001), P.enamel(C.bronze), NO);
    add(S.tube(870, 1192, 352, 22, 14), cream);
    for (const x of [1100, 1110, 1120]) add(S.tube(x, x + 4, 352, 23.5, 14), shade(cream, 0.6), NO);
    add(S.tube(1190, 1268, 352, 10, 10), P.metal);
    bore(add, 0.003, S.z(1268), 0, S.y(352));
    add(S.blk(745, 425, 820, 452, 0.02, 0.002), plum);
    // the ivory grip and the guard
    add(S.slab([[198, 470], [250, 478], [226, 540], [206, 600], [206, 690], [166, 682], [106, 604], [96, 566], [150, 520]], 0.032, 0, 0.004), ivory);
    for (const sx of [1, -1]) add(T(new THREE.CircleGeometry(S.l(14), 12), sx * 0.0162, S.y(586), S.z(170), 0, sx > 0 ? PI / 2 : -PI / 2, 0), P.enamel(C.orange), NO);
    add(guard(S.y(514), S.z(300), S.l(44), 0.0026), P.metal, NO);
    trigger(add, P.metal, S.y(520), S.z(312));
  });
  const fins = g.part((add) => {
    for (let i = 0; i < 6; i++) add(S.tube(1196 + i * 12, 1199 + i * 12, 352, 50 - (i % 2) * 6, 18), P.enamel(C.bronze), { glow: C.tealGlow, chip: 0.2, outline: false });
  }, 'round');
  const vial = g.part((add) => {
    add(cz(S.l(52), S.l(52), S.z(322), S.z(434), 14, 0, VY), C.tealDk, { glow: C.tealGlow, outline: false, rust: 0 });
    for (const x of [322, 434]) add(cz(S.l(56), S.l(56), S.z(x) - 0.003, S.z(x) + 0.003, 14, 0, VY), P.brass, NO);
  }, 'none');
  const lid = g.part((add) => {
    add(S.blk(268, 286, 440, 312, 0.038, 0.002), cream);
    add(S.blk(380, 278, 420, 286, 0.012, 0.001), P.brass, NO);
  });
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(352), S.z(1270)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      const full = a.charge >= 0.99 ? 1 : 0;
      s.part(fins, IDENT, full * (1.4 + 0.4 * Math.sin(a.t * 22)) + a.charge * 0.35 + (a.shotAge < 0.25 ? 1.5 * (1 - a.shotAge / 0.25) : 0));
      const rl = a.reload > 0.4, u = a.reloadU;
      s.part(lid, rl ? about(0, S.y(299), S.z(268), 1, 0, 0, -1.25 * pulse(u, 0.06, 0.16, 0.74, 0.84)) : IDENT, 0);
      let gl = 0.35 + a.charge * 1.6;
      if (!rl) { s.part(vial, IDENT, gl); return; }
      if (u < 0.18) s.part(vial, IDENT, gl);
      else if (u < 0.32) { const k = ramp(u, 0.18, 0.32); s.part(vial, moved(0.02 * k, 0.09 * k, -0.02 * k), 0.1); }
      else if (u > 0.56) { gl = u > 0.8 ? 0.35 + 1.2 * (1 - ramp(u, 0.8, 0.98)) : 0.25; s.part(vial, moved(0, 0.08 * (1 - ramp(u, 0.6, 0.72)), 0), gl); }
    },
  };
}

// ================================================================== AKIMBO PISTOLS
// Traced from akimbo.png (the right-hand pistol of the pair): a mustard receiver with a dark rail,
// a front cap and a stub muzzle, a rear block with the sensor eye and a wire loop over it, the grip
// under it, one long curved magazine out of the front well. The eye blinks while the tracking
// rounds fly; the cocking knob jumps per shot; the mag dumps on the flick.
function akimbo(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(772, 300, 0.0005);
  const mus = P.enamel(C.mustard), musDk = shade(mus, 0.74), plum = P.enamel(C.maroon);
  const body = g.part((add) => {
    add(S.blk(838, 152, 1092, 300, 0.034, 0.002), mus);
    add(S.blk(838, 262, 1092, 276, 0.0345, 0.001), musDk, NO);
    add(S.blk(1090, 150, 1172, 250, 0.036, 0.002), mus);
    add(S.blk(1100, 150, 1110, 250, 0.037, 0.0008), musDk, NO);
    add(S.tube(1170, 1246, 203, 13, 10), P.steel);
    add(S.tube(1170, 1190, 203, 17, 10), C.dark);
    bore(add, 0.004, S.z(1246), 0, S.y(203));
    add(S.slab([[1130, 152], [1142, 152], [1140, 122], [1132, 122]], 0.005), mus);
    add(S.slab([[1142, 250], [1152, 250], [1158, 272], [1148, 274]], 0.004), mus, NO);
    // rear block, grip, wire loop
    add(S.blk(712, 155, 840, 300, 0.036, 0.002), mus);
    add(S.slab([[712, 296], [838, 296], [838, 494], [706, 494], [708, 442], [732, 380], [722, 330]], 0.032, 0, 0.002), mus);
    add(S.blk(704, 488, 842, 500, 0.034, 0.001), musDk);
    for (const sx of [1, -1]) {
      add(S.slab([[730, 330], [826, 330], [826, 470], [718, 470], [736, 390]], 0.002, sx * 0.0166, 0.0004), musDk, NO);
      add(ball(0.0026, sx * 0.0172, S.y(470), S.z(735), 6, 4), P.metal, NO);
    }
    add(rod(S.p(700, 250), S.p(700, 130), 0.003), mus);
    add(rod(S.p(700, 130), S.p(830, 130), 0.003), mus);
    add(rod(S.p(830, 130), S.p(830, 155), 0.003), mus);
    // front mag well, guard, trigger
    add(S.slab([[920, 296], [1030, 296], [1030, 430], [1000, 450], [1000, 495], [928, 495], [920, 450]], 0.03, 0, 0.002), mus);
    add(S.blk(846, 300, 940, 306, 0.01, 0.001), P.metal, NO);
    add(rod(S.p(846, 300), S.p(846, 362), 0.0022), P.metal);
    add(rod(S.p(846, 362), S.p(936, 362), 0.0022), P.metal);
    trigger(add, P.metal, S.y(328), S.z(878));
    for (const sx of [1, -1] as const) add(sideDecal(0.03, 0.012, sx, sx * 0.0172, S.y(352), S.z(985)), 0xffffff, DEC(DC.warn));
    for (const sx of [1, -1] as const) add(sideDecal(0.018, 0.018, sx, sx * 0.0182, S.y(362), S.z(788)), 0xffffff, DEC(DC.price));
  });
  // one long curved magazine out of the front well
  const mag = g.part((add) => {
    add(S.slab([[938, 470], [1012, 470], [1014, 560], [1030, 660], [1052, 762], [1004, 788], [986, 760], [966, 660], [950, 560]], 0.022, 0, 0.002), plum);
    add(S.slab([[958, 520], [966, 520], [990, 700], [982, 700]], 0.0228, 0, 0.0003), shade(plum, 0.6), NO);
    add(S.slab([[996, 778], [1056, 756], [1060, 768], [1000, 792]], 0.026, 0, 0.001), P.metal);
  });
  const bolt = g.part((add) => {
    add(S.drum(1025, 274, 9, 0.042, 10), P.metal);
    add(S.drum(1025, 274, 6, 0.05, 8), P.brass);
  }, 'none');
  const eye = g.part((add) => {
    for (const sx of [1, -1]) {
      add(ringX(S.l(22), 0.0024, sx * 0.0182, S.y(198), S.z(762), 14), P.metal, NO);
      add(ball(S.l(15), sx * 0.0176, S.y(198), S.z(762), 10, 7, 0.4, 1, 1), C.dark, { glow: C.orangeGlow, outline: false });
    }
  }, 'none');
  const drop = new THREE.Vector3(0, -1, 0.15).normalize();
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(203), S.z(1248)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      s.part(bolt, moved(0, 0, -0.022 * Math.min(1, kick(a.shotAge, 0.07))), 0);
      const blink = a.shotAge < 0.8 ? (Math.sin(a.t * 30 + (a.twin ? 1.7 : 0)) > 0 ? 1.6 : 0.3) : 0.35;
      s.part(eye, IDENT, blink);
      const rl = a.reload > 0.4, u = a.reloadU;
      if (!rl || u < 0.16 || u > 0.72) { s.part(mag, IDENT, 0); return; }
      if (u < 0.32) { const d = ramp(u, 0.16, 0.32) * 0.26; s.part(mag, moved(drop.x * d, drop.y * d, drop.z * d), 0); }
      else if (u > 0.5) { const d = (1 - ramp(u, 0.56, 0.72)) * 0.12; s.part(mag, moved(drop.x * d, drop.y * d, drop.z * d), 0); }
    },
  };
}

// ================================================================== THE SCOUNDREL
// Traced from scoundrel.png with its barrels sawn far shorter (ragged ends) and the red toned
// down: a brass receiver with the hammer spur, over-under barrels with a mustard stripe and
// stickers, the forend under them, a swept grip wrapped in cord. One-handed reload: a wrist flick
// drops the barrels open and the shells fly, two fresh ones, flick it shut.
function scoundrel(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(262, 562, 0.00052);
  const burnt = P.enamel(C.burnt), plum = P.enamel(C.plum), mus = P.enamel(C.mustard);
  const PZ = S.z(556), PY = S.y(545), CUT = 1000, TY = 370, BY = 475;
  const body = g.part((add) => {
    // receiver, hammer spur
    add(S.slab([[345, 332], [520, 332], [552, 318], [556, 545], [460, 548], [345, 548]], 0.048, 0, 0.0025), P.brass);
    add(S.blk(360, 360, 520, 380, 0.0485, 0.0012), shade(P.brass, 0.7), NO);
    for (const sx of [1, -1]) for (const [px, py] of [[500, 510], [372, 470], [440, 400]] as const) add(ball(0.0034, sx * 0.0242, S.y(py), S.z(px), 6, 4), P.metal, NO);
    add(S.slab([[270, 312], [300, 316], [342, 340], [342, 375], [312, 372], [300, 335]], 0.016), P.metal);
    // grip: swept back, burnt enamel, cord-wrapped, a plum heel
    add(S.slab([[205, 418], [345, 380], [345, 548], [330, 562], [300, 726], [180, 726], [165, 690], [196, 580], [236, 490], [222, 450]], 0.042, 0, 0.003), burnt);
    for (let i = 0; i < 9; i++) {
      const t = i / 8, y0 = 565 + t * 150, x0 = 198 - t * 26, x1 = 328 - t * 26;
      add(S.slab([[x0, y0], [x1, y0 - 4], [x1, y0 + 12], [x0, y0 + 16]], 0.046, 0, 0.002), shade(C.cord, i % 2 ? 1 : 0.8), NO);
    }
    add(S.blk(178, 716, 302, 730, 0.044, 0.002), plum);
    add(guard(S.y(548), S.z(458), S.l(70), 0.0034), P.metal, NO);
    trigger(add, P.metal, S.y(575), S.z(418));
  });
  const barrels = g.part((add) => {
    for (const y of [TY, BY]) {
      add(S.tube(556, CUT, y, 45, 18), burnt);
      add(S.tube(CUT - 14, CUT, y, 47, 18), shade(burnt, 0.8));
      bore(add, S.l(32), S.z(CUT), 0, S.y(y));
      // ragged sawn end
      for (let k = 0; k < 9; k++) {
        const an = (k / 9) * PI * 2 + y;
        add(box(0.008, 0.006, 0.008 + (k % 3) * 0.005, Math.cos(an) * S.l(46), S.y(y) + Math.sin(an) * S.l(46), S.z(CUT) - 0.002 + (k % 2) * 0.004, 0, 0, an), P.steel, NO);
      }
    }
    add(S.blk(556, 320, CUT - 6, 340, 0.04, 0.002), mus);
    add(S.blk(556, 420, CUT - 4, 428, 0.05, 0.0008), plum, NO);
    add(S.slab([[556, 520], [CUT - 20, 520], [CUT - 20, 548], [600, 556], [556, 556]], 0.05, 0, 0.002), plum);
    add(S.blk(556, 318, 580, 530, 0.106, 0.002), P.brass);
    add(S.slab([[CUT - 40, 302], [CUT - 18, 302], [CUT - 18, 322], [CUT - 40, 322]], 0.01), P.metal);
    for (const sx of [1, -1] as const) {
      add(sideDecal(0.03, 0.022, sx, sx * S.l(49.5), S.y(468), S.z(640)), 0xffffff, DEC(DC.sale));
      add(sideDecal(0.022, 0.022, sx, sx * S.l(49.5), S.y(470), S.z(760)), 0xffffff, DEC(DC.hanko));
    }
  }, 'box');
  const shells = g.part((add) => {
    add(cz(S.l(48), S.l(48), PZ - 0.004, PZ + 0.008, 12, 0, 0), P.brass);
    add(cz(S.l(44), S.l(44), PZ + 0.008, PZ + 0.066, 12, 0, 0), P.enamel(C.teal));
  }, 'none', 2);
  const SY = [S.y(TY), S.y(BY)];
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, (SY[0] + SY[1]) / 2, S.z(CUT + 2)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      const rl = a.reload > 0.3, u = a.reloadU;
      // the flick: falls open early, snaps shut late
      const open = rl ? Math.min(smooth((a.reload - 0.3) / 0.4), ramp(u, 0.04, 0.12) * (1 - ramp(u, 0.74, 0.8))) : 0;
      const hinge = about(0, PY, PZ, 1, 0, 0, 0.75 * open, 0, 0, 0, M1);
      s.part(barrels, hinge, 0);
      for (let i = 0; i < 2; i++) {
        M3.makeTranslation(0, SY[i], 0);
        if (!rl) { s.part(shells + i, M2.multiplyMatrices(hinge, M3), 0); continue; }
        if (u < 0.32) {
          const k = ramp(u, 0.12, 0.32);
          if (k < 0.98) s.part(shells + i, M2.multiplyMatrices(hinge, M3.makeTranslation((i ? -1 : 1) * 0.05 * k, SY[i] + 0.1 * k - 0.16 * k * k, -0.2 * k)), 0);
        } else if (u > 0.5) {
          const k = 1 - ramp(u, 0.52 + i * 0.06, 0.64 + i * 0.06);
          s.part(shells + i, M2.multiplyMatrices(hinge, M3.makeTranslation(0, SY[i] + 0.03 * k, -0.08 * k)), 0);
        }
      }
    },
  };
}

// ================================================================== ROCKET LAUNCHERS
// One base model traced from rocket_launcher.png (the water-heater tank with rounded shoulders, a
// flared back-blast cone behind a collar, a narrower front section, the striped rocket nose out
// the front until it's fired, a periscope sight on an arm, valves at the back, a shoulder rest)
// and a skin per element.
interface RocketSkin { body: Num3; cone: number; nose: number; noseGlow: number; extra?: (add: Add, P: Paint) => void; spark?: (add: Add) => void }
const RS = new Sheet(530, 470, 0.00095);
/** the tank's axis height, its span down Z (tank + front section), its radius */
const TY = RS.y(326), TZ0 = RS.z(430), TZ1 = RS.z(1122), TR = RS.l(100);
const tAlong = (z: number) => (z - TZ0) / (TZ1 - TZ0);
function rocketSkin(elem: number, P: Paint): RocketSkin {
  const cream = P.enamel(C.cream);
  const ang = (x: number, y: number) => Math.atan2(y - TY, x);
  switch (elem) {
    case Elem.Fire: {
      const fire = P.enamel(C.fire), burnt = P.enamel(C.burnt);
      return {
        body: (x, y, z) => {
          // flames licking forward from the back: tongues round the tube, a burnt core behind them
          const t = tAlong(z), a = ang(x, y), lick = 0.42 + 0.17 * Math.sin(a * 6) + 0.08 * Math.sin(a * 13 + 1.3);
          return t < lick - 0.2 ? burnt : t < lick ? fire : cream;
        },
        cone: burnt, nose: P.enamel(C.orange), noseGlow: C.fireGlow,
      };
    }
    case Elem.Ice: {
      const ice = P.enamel(C.ice), frost = P.enamel(0xeef6f6);
      return {
        body: (x, y) => (y > TY + TR * 0.7 + 0.012 * Math.sin(x * 140) ? frost : ice),
        cone: shade(ice, 0.82), nose: frost, noseGlow: C.iceGlow,
        extra: (add) => {
          for (let i = 0; i < 9; i++) add(ball(0.02 + (i % 3) * 0.004, (i % 2 ? 0.02 : -0.025) * 0.6, TY + TR * 0.97, TZ0 + 0.04 + i * (TZ1 - TZ0 - 0.08) / 8, 7, 5, 1.6, 0.3, 1.5), frost, NO);
          const CZ = RS.z(100), CR = RS.l(112);
          for (let i = 0; i < 7; i++) { const an = PI + 0.25 + i * 0.4; add(T(new THREE.ConeGeometry(0.007, 0.06 + (i % 3) * 0.025, 5), Math.cos(an) * CR, TY + Math.sin(an) * CR - 0.03, CZ + 0.02 + (i % 2) * 0.02, PI), frost, NO); }
          for (let i = 0; i < 5; i++) add(T(new THREE.ConeGeometry(0.007, 0.03 + (i % 2) * 0.02, 5), (i % 2 ? 0.02 : -0.03), TY - TR - 0.01, TZ0 + 0.08 + i * 0.12, PI), frost, NO);
        },
      };
    }
    case Elem.Shock: {
      const yel = P.enamel(C.shock);
      // the twin strapped capacitor pods ride the top of the tank, the copper coils stand up front
      const PY = TY + TR * 0.88, PX = 0.05, Z0 = TZ0 + 0.04, Z1 = TZ0 + 0.34;
      return {
        body: yel, cone: P.metal, nose: P.enamel(C.orange), noseGlow: C.shockGlow,
        extra: (add) => {
          for (const sx of [1, -1]) {
            add(cz(0.03, 0.03, Z0, Z1, 12, sx * PX, PY), P.enamel(C.cream2));
            for (const z of [Z0, Z1]) add(cz(0.032, 0.032, z - 0.006, z + 0.006, 12, sx * PX, PY), P.metal, NO);
            for (let i = 0; i < 5; i++) add(ringZ(0.032, 0.004, Z0 + 0.05 + i * 0.05, sx * PX, PY, 12), P.enamel(C.copper), NO);
          }
          for (const z of [TZ1 - 0.2, TZ1 - 0.08]) {
            add(cy(0.008, 0.06, 8, 0, TY + TR * 0.85 + 0.02, z), P.metal);
            for (let i = 0; i < 4; i++) add(T(new THREE.TorusGeometry(0.016, 0.004, 4, 12), 0, TY + TR * 0.85 + 0.006 + i * 0.011, z, PI / 2), P.enamel(C.copper), NO);
          }
        },
        spark: (add) => {
          const y = TY + TR * 0.85 + 0.045, z0 = TZ1 - 0.2, z1 = TZ1 - 0.08, d = (z1 - z0) / 4;
          const pts: [number, number, number][] = [[0, y, z0], [0.012, y + 0.02, z0 + d], [-0.01, y + 0.01, z0 + 2 * d], [0.008, y + 0.025, z0 + 3 * d], [0, y, z1]];
          for (let i = 0; i < pts.length - 1; i++) add(rod(pts[i], pts[i + 1], 0.0028, 4), C.shockGlow, { glow: C.shockGlow, outline: false, rust: 0, chip: 0 });
        },
      };
    }
    case Elem.Acid: {
      const acid = P.enamel(0x8c9c40), acidDk = shade(acid, 0.62);
      return {
        body: (x, y, z) => {
          const t = tAlong(z);
          const n = Math.sin(x * 61 + z * 17) * Math.sin(y * 47 - z * 29) + Math.sin(z * 41 + x * 13) * 0.5;
          const eat = (1 - t) * 1.7 - 0.55;
          return n < eat - 0.55 ? acidDk : n < eat ? acid : cream;
        },
        cone: acid, nose: acid, noseGlow: C.acidGlow,
        extra: (add) => {
          for (let i = 0; i < 8; i++) add(T(new THREE.ConeGeometry(0.006 + (i % 2) * 0.003, 0.03 + (i % 3) * 0.02, 5), ((i * 37) % 7 - 3) * 0.012, TY - TR - (i % 3) * 0.01, TZ0 + 0.03 + i * 0.045, PI), acid, { outline: false, glow: C.acidGlow });
        },
      };
    }
    case Elem.Magnetic: {
      const mag = P.enamel(0x7d5aa8), MZ = (TZ0 + TZ1) / 2 - 0.04, MX = -(TR + 0.02);
      return {
        body: mag, cone: shade(mag, 0.7), nose: P.enamel(C.steel), noseGlow: C.magGlow,
        extra: (add) => {
          add(T(new THREE.TorusGeometry(0.075, 0.024, 6, 16, PI), MX, TY - 0.01, MZ, 0, PI / 2, PI), P.enamel(0xb24a5a));
          for (const z of [MZ - 0.075, MZ + 0.075]) add(rb(0.044, 0.046, 0.046, 0.004, MX, TY - 0.01, z), P.enamel(C.steel));
        },
      };
    }
    default:
      return { body: cream, cone: P.enamel(C.burnt), nose: cream, noseGlow: C.orangeGlow };
  }
}

function rocketLauncher(P: Paint, elem: number): GunModel {
  const g = new GB();
  const S = RS, sk = rocketSkin(elem, P), plum = P.enamel(C.plum), FR = S.l(86);
  const body = g.part((add) => {
    // the tank (rounded shoulders) and the front section, in many rings so the skins can change
    // along it
    const tank: [number, number][] = [[S.l(56), S.z(430)], [S.l(84), S.z(440)], [TR * 0.97, S.z(462)], [TR, S.z(482)]];
    for (let i = 1; i < 28; i++) tank.push([TR, S.z(482 + i * 15)]);
    tank.push([TR, S.z(905)], [TR * 0.96, S.z(935)], [FR, S.z(955)]);
    for (let i = 1; i < 10; i++) tank.push([FR, S.z(955 + i * 16)]);
    tank.push([FR, S.z(1112)], [FR * 0.92, S.z(1122)]);
    add(lathe(tank, 32, 0, TY), sk.body, { rust: 0.1 });
    for (const x of [508, 905]) add(S.tube(x, x + 6, 326, 101, 24), shade(P.metal, 0.9), NO);
    add(S.tube(950, 960, 326, 92, 22), P.metal, NO);
    for (let i = 0; i < 14; i++) { const an = PI * 0.15 + (i / 13) * PI * 0.7; add(ball(0.004, Math.cos(an) * TR, TY + Math.sin(an) * TR, S.z(560), 6, 4), P.metal, NO); add(ball(0.004, Math.cos(an) * TR, TY + Math.sin(an) * TR, S.z(870), 6, 4), P.metal, NO); }
    // neck, collar, back-blast cone
    add(S.tube(388, 432, 326, 60, 18), P.metal);
    add(S.tube(342, 390, 326, 76, 20), shade(P.metal, 1.15));
    add(lathe([[S.l(70), S.z(342)], [S.l(84), S.z(270)], [S.l(102), S.z(160)], [S.l(114), S.z(98)]], 22, 0, TY), sk.cone);
    add(inward(lathe([[S.l(64), S.z(344)], [S.l(78), S.z(270)], [S.l(96), S.z(160)], [S.l(109), S.z(100)]], 22, 0, TY)), C.dark, NO);
    add(ringZ(S.l(114), 0.005, S.z(98), 0, TY, 22), P.metal, NO);
    // the bore at the front
    add(S.tube(1118, 1128, 326, 60, 18), C.dark, NO);
    // periscope sight on its arm
    add(S.slab([[935, 238], [1050, 214], [1050, 240], [940, 252]], 0.014), P.metal);
    add(S.blk(1050, 185, 1118, 240, 0.02, 0.002), P.metal);
    add(S.tube(1055, 1150, 154, 30, 14, -0.004), P.enamel(C.cream2));
    add(S.tube(1148, 1152, 154, 26, 14, -0.004), C.glass, NO);
    add(ringZ(S.l(24), 0.003, S.z(1030), -0.004, S.y(155), 14), P.metal, NO);
    add(S.tube(1090, 1100, 154, 32, 14, -0.004), P.enamel(C.orange), NO);
    // valves and a gauge at the back
    add(S.blk(340, 400, 395, 455, 0.04, 0.003), P.metal);
    add(S.drum(368, 425, 22, 0.05, 12), P.metal);
    add(S.drum(420, 425, 14, 0.05, 12), P.brass);
    add(cx(S.l(26), 0.012, 14, TR * 0.55, S.y(380), S.z(700)), P.brass);
    add(sideDecal(S.l(44), S.l(44), 1, TR * 0.55 + 0.0062, S.y(380), S.z(700)), 0xffffff, DEC(DC.dial));
    // grip under the tank, the trigger housing, a shoulder rest under the collar
    add(S.blk(460, 430, 722, 498, 0.05, 0.003), P.metal);
    add(S.slab([[492, 496], [560, 498], [545, 560], [528, 600], [486, 594], [496, 556]], 0.036, 0, 0.003), plum);
    add(S.blk(664, 496, 724, 560, 0.04, 0.002), P.metal);
    for (const y of [512, 530, 548]) add(S.blk(660, y, 728, y + 4, 0.042, 0.0005), shade(P.metal, 0.6), NO);
    add(guard(S.y(500), S.z(600), 0.02, 0.0034), P.metal, NO);
    trigger(add, P.metal, S.y(512), S.z(592));
    add(S.slab([[300, 420], [470, 420], [470, 470], [320, 476]], 0.07, 0, 0.004), plum);
    add(S.blk(740, 425, 800, 445, 0.03, 0.002), P.metal);
    for (const sx of [1, -1] as const) add(sideDecal(0.13, 0.03, sx, sx * (FR + 0.001), TY - 0.02, S.z(1040)), 0xffffff, DEC(DC.stripes));
    for (const sx of [1, -1] as const) add(sideDecal(0.06, 0.04, sx, sx * (TR + 0.001), TY + 0.01, S.z(640)), 0xffffff, DEC(DC.patch));
    sk.extra?.(add, P);
  });
  const rocket = g.part((add) => {
    add(lathe([[S.l(54), S.z(1110)], [S.l(54), S.z(1280)], [S.l(46), S.z(1340)], [S.l(28), S.z(1410)], [S.l(4), S.z(1460)]], 18, 0, TY), sk.nose, { glow: sk.noseGlow });
    for (let i = 0; i < 3; i++) add(T(new THREE.TorusGeometry(S.l(54.5), 0.0028, 3, 18, PI * 0.7), 0, TY, S.z(1170 + i * 30), 0.35, 0, 0.5), P.enamel(C.burnt), NO);
    add(S.tube(1276, 1284, 326, 56, 18), P.metal, NO);
  }, 'round');
  const spark = sk.spark ? g.part(sk.spark, 'none') : -1;
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, TY, S.z(1125)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      // the one rocket: gone once fired, slides back in when the launcher has another
      if (a.loaded > 0 && a.shotAge > 0.6) s.part(rocket, moved(0, 0, -0.2 * (1 - ramp(a.shotAge, 0.6, 0.9))), elem === Elem.Fire ? 0.18 + 0.1 * Math.sin(a.t * 5) : 0.05);
      if (spark >= 0 && a.loaded > 0 && Math.sin(a.t * 37) + Math.sin(a.t * 23) > 0.3) s.part(spark, M1.makeScale(1, 1 + 0.4 * Math.sin(a.t * 51), 1), 1.5);
    },
  };
}

// ================================================================== HARPOON GUN
// Traced from harpoon.png (flipped to fire right, the rear spike dropped): a sea-green tube with
// a cream top band, the brass harpoon head out the front (barbed), a scrolled pistol grip, the gas
// canister and the copper cable reel slung underneath. The reel pays out on the shot and winds
// back as the next bolt slides home.
function harpoon(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(290, 402, 0.00075);
  const sea = P.enamel(C.seagreen), copper = P.enamel(C.copper), cream = P.enamel(C.cream), HY = S.y(326);
  const RZ = S.z(700), RY = S.y(432);
  const body = g.part((add) => {
    add(S.blk(222, 272, 980, 380, 0.07, 0.003), sea);
    add(S.blk(222, 270, 980, 298, 0.0705, 0.002), cream);
    for (const x of [370, 560]) add(S.blk(x, 272, x + 6, 380, 0.0708, 0.0006), shade(sea, 0.7), NO);
    add(S.blk(960, 286, 1000, 372, 0.06, 0.002), P.metal);
    add(S.blk(160, 300, 225, 352, 0.05, 0.002), P.metal);
    add(S.blk(360, 378, 800, 396, 0.04, 0.002), P.metal);
    // sights
    add(S.slab([[275, 270], [440, 262], [452, 222], [460, 226], [445, 270]], 0.008), P.metal);
    add(S.slab([[946, 272], [960, 272], [958, 212], [952, 212]], 0.005), P.metal);
    // scrolled pistol grip
    add(S.slab([[245, 365], [332, 384], [334, 440], [292, 480], [252, 500], [210, 548], [186, 568], [134, 546], [170, 480], [204, 440], [226, 396]], 0.04, 0, 0.004), sea);
    for (const sx of [1, -1]) add(ball(0.004, sx * 0.0205, S.y(462), S.z(246), 8, 5), copper, NO);
    add(guard(S.y(440), S.z(355), S.l(36), 0.003), P.metal, NO);
    trigger(add, P.metal, S.y(452), S.z(350));
    // gas canister with its valve
    add(S.tube(432, 568, 428, 28, 14), P.steel);
    for (const x of [450, 545]) add(S.tube(x, x + 6, 428, 30, 14), P.brass, NO);
    add(S.tube(568, 598, 428, 10, 8), P.brass);
    // reel housing and bracket
    add(S.tube(600, 726, 432, 47, 18), sea);
    add(S.tube(790, 806, 432, 47, 18), P.metal);
    add(S.blk(620, 392, 780, 402, 0.03, 0.001), P.metal);
    for (const sx of [1, -1] as const) add(sideDecal(0.05, 0.05, sx, sx * 0.0354, HY, S.z(520)), 0xffffff, DEC(DC.warn));
  });
  const spear = g.part((add) => {
    add(cz(0.0065, 0.0065, S.z(240), S.z(990), 6, 0, HY), P.brass);
    add(lathe([[S.l(26), S.z(985)], [S.l(20), S.z(1080)], [S.l(10), S.z(1220)], [S.l(1), S.z(1316)]], 10, 0, HY), P.brass);
    for (let i = 0; i < 3; i++) { const an = (i / 3) * PI * 2; add(rod([Math.cos(an) * 0.012, HY + Math.sin(an) * 0.012, S.z(1120)], [Math.cos(an) * 0.04, HY + Math.sin(an) * 0.04, S.z(1040)], 0.006, 4, 0.0015), P.steel, NO); }
    add(ringZ(0.012, 0.003, S.z(990), 0, HY, 10), copper, NO);
  }, 'none');
  // the copper coil on its spool (spins on X)
  const reel = g.part((add) => {
    add(cz(S.l(40), S.l(40), -S.l(32), S.l(32), 16), copper);
    for (let i = 0; i < 6; i++) add(ringZ(S.l(41), 0.0018, -S.l(28) + i * S.l(11), 0, 0, 16), shade(copper, 0.65), NO);
    for (const z of [-S.l(34), S.l(34)]) add(cz(S.l(47), S.l(47), z - 0.002, z + 0.002, 16), P.metal);
  }, 'round');
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, HY, S.z(1000)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      const back = 1 - ramp(a.shotAge, 0.75, 1.15);
      if (a.loaded > 0 && a.shotAge > 0.75) s.part(spear, moved(0, 0, -0.3 * back), 0);
      const spin = a.shotAge < 0.4 ? a.shotAge * 30 : a.shotAge < 1.15 ? 12 - (a.shotAge - 0.4) * 22 : -4.5;
      s.part(reel, about(0, 0, 0, 0, 0, 1, spin, 0, RY, S.z(758)), 0);
    },
  };
}

// ================================================================== THE BIG ONE
// Traced from big_one.png at thermos size: a tall red egg with a cream warning band, a cream
// skirt and a dark machined base, a cap on top with the blinking lens and the antenna that fires
// the marker laser, a pipe down one flank. Held in the hand, lobbed.
const BIG_RED = 0xcf4a2e;
function bigOneParts(g: GB, P: Paint) {
  const red = P.enamel(BIG_RED), cream = P.enamel(C.cream), dark = P.enamel(C.plumDk), CZ0 = 0.05;
  const s = 0.00046, oy = 676, X = (px: number) => (px - 500) * s, Y = (py: number) => (oy - py) * s;
  const body = g.part((add) => {
    const prof: [number, number][] = [[2, 342], [70, 344], [112, 352], [142, 372], [160, 420]];
    for (let py = 430; py <= 780; py += 10) prof.push([Math.min(172, 160 + (py - 420) * 0.15), py]);
    prof.push([166, 790]);
    const egg = new THREE.LatheGeometry(prof.map(([r, py]) => new THREE.Vector2(Math.max(r * s, 1e-3), Y(py))).reverse(), 24);
    egg.translate(0, 0, CZ0);
    const band = (y: number) => (y < Y(430) && y > Y(468)) || y < Y(700) ? cream : red;
    add(egg, (_x, y) => band(y), { rust: 0.12 });
    for (const py of [440, 712]) add(T(new THREE.TorusGeometry(X(500 + (py < 500 ? 150 : 162)), 0.0025, 4, 24), 0, Y(py), CZ0, PI / 2), shade(BIG_RED, 0.75), NO);
    // the machined base under the skirt
    const base: [number, number][] = [[166, 790], [156, 820], [122, 862], [86, 905], [80, 950], [2, 952]];
    const bg = new THREE.LatheGeometry(base.map(([r, py]) => new THREE.Vector2(Math.max(r * s, 1e-3), Y(py))).reverse(), 20);
    bg.translate(0, 0, CZ0);
    add(bg, dark);
    for (let i = 0; i < 4; i++) { const an = (i / 4) * PI * 2 + PI / 4; add(box(0.018, 0.03, 0.026, Math.cos(an) * X(640), Y(820), CZ0 + Math.sin(an) * X(640), 0, -an), P.metal); }
    for (let i = 0; i < 12; i++) { const an = (i / 12) * PI * 2; add(ball(0.0032, Math.cos(an) * X(662), Y(560), CZ0 + Math.sin(an) * X(662), 6, 4), P.metal, NO); }
    // the cap and its gear, antennas, the pipe down the flank
    add(T(new THREE.CylinderGeometry(X(580), X(590), 0.014, 18), 0, Y(330), CZ0), dark);
    add(T(new THREE.CylinderGeometry(X(560), X(575), 0.022, 16), 0, Y(305), CZ0), red);
    add(T(new THREE.CylinderGeometry(X(540), X(555), 0.01, 14), 0, Y(268), CZ0), dark);
    add(box(0.03, 0.02, 0.026, X(450), Y(298), CZ0 + 0.02), P.metal);
    add(cy(0.0018, Y(100) - Y(262), 4, 0, (Y(100) + Y(262)) / 2, CZ0), P.steel, NO);
    add(cy(0.0014, Y(215) - Y(262), 4, X(550), (Y(215) + Y(262)) / 2, CZ0 - 0.01), P.steel, NO);
    add(rod([X(600), Y(290), CZ0 - 0.02], [X(615), Y(340), CZ0 - 0.04], 0.003), C.dark, NO);
    add(rod([X(338), Y(762), CZ0 + 0.03], [X(338), Y(905), CZ0 + 0.03], 0.005), P.brass);
    add(box(0.016, 0.016, 0.02, X(350), Y(770), CZ0 + 0.03), P.metal);
    add(box(0.02, 0.014, 0.02, X(680), Y(768), CZ0), P.metal);
  }, 'round');
  const lens = g.part((add) => {
    add(ball(0.011, X(520), Y(300), CZ0 + X(555) * 0.9, 10, 7), 0x6a1d18, { glow: 0xff4a3a, outline: false });
  }, 'none');
  return { body, lens, muzzle: new THREE.Vector3(0, Y(100), CZ0) };
}
function bigOne(P: Paint): GunModel {
  const g = new GB();
  const p = bigOneParts(g, P);
  return {
    rig: g.rig, muzzle: p.muzzle,
    pose(a, s) {
      if (a.shotAge < 0.8) return; // lobbed: the hand is empty until the next one is ready
      s.part(p.body, IDENT, 0);
      s.part(p.lens, IDENT, Math.sin(a.t * 6) > 0 ? 1.7 : 0.25);
    },
  };
}

// ================================================================== BAD IDEA
// Traced from bad_idea.png: two fat rockets duct-taped to a boxy launcher, their mismatched noses
// pointing back past your shoulder (the gag: it still fires forward), warning stickers
// everywhere, a long thin barrel with a brake out the front, a radar dish on a mast that sweeps for
// the biggest robot in sight. The mounting foot became a front handle.
function badIdea(P: Paint): GunModel {
  const g = new GB();
  const S = new Sheet(880, 492, 0.0009);
  const body0 = P.enamel(C.burnt), cream = P.enamel(C.cream), plum = P.enamel(C.plum);
  const NOSE = [C.ice, C.shock, C.acid];
  const body = g.part((add) => {
    // the launcher block: upper box, receiver, collar
    add(S.blk(460, 238, 720, 330, 0.11, 0.004), body0);
    add(S.blk(620, 236, 884, 330, 0.09, 0.004), body0);
    add(S.blk(700, 320, 1005, 432, 0.1, 0.004), body0);
    add(S.blk(700, 380, 1005, 396, 0.102, 0.001), plum, NO);
    add(S.blk(1000, 352, 1110, 424, 0.07, 0.003), plum);
    add(S.blk(460, 330, 720, 522, 0.1, 0.004), shade(body0, 0.85));
    add(S.blk(700, 430, 840, 520, 0.08, 0.003), plum);
    for (const sx of [1, -1] as const) {
      add(sideDecal(S.l(70), S.l(48), sx, sx * 0.0505, S.y(300), S.z(790)), 0xffffff, DEC(DC.warn));
      add(sideDecal(S.l(90), S.l(40), sx, sx * 0.0565, S.y(275), S.z(520)), 0xffffff, DEC(DC.sale));
      add(sideDecal(S.l(70), S.l(50), sx, sx * 0.0505, S.y(410), S.z(900)), 0xffffff, DEC(DC.barcode));
    }
    // barrel and brake, the front sight
    add(S.tube(1105, 1405, 392, 10, 10), P.brass);
    add(S.tube(1326, 1396, 392, 15, 10), P.brass);
    add(S.tube(1105, 1125, 392, 20, 12), P.metal);
    bore(add, 0.005, S.z(1406), 0, S.y(392));
    add(S.slab([[1120, 380], [1130, 380], [1130, 310], [1124, 310]], 0.005), P.metal);
    // grip, trigger guard, the front handle
    add(S.slab([[842, 488], [912, 490], [918, 560], [904, 578], [880, 574], [870, 540], [842, 520]], 0.036, 0, 0.003), plum);
    add(guard(S.y(500), S.z(930), 0.024, 0.0034), P.metal, NO);
    trigger(add, P.metal, S.y(510), S.z(918));
    add(S.slab([[1040, 424], [1086, 424], [1080, 532], [1044, 532]], 0.032, 0, 0.003), plum);
    // the dish's mast
    add(S.blk(682, 200, 720, 240, 0.03, 0.002), P.metal);
    add(S.blk(700, 192, 780, 204, 0.04, 0.002), body0);
    add(S.tube(690, 712, 170, 8, 8), P.metal);
  });
  // the rockets, noses back, wrapped in tape
  const rockets = g.part((add) => {
    const R: [number, number, number, number][] = [[305, 55, 122, 700], [455, 62, 150, 700], [480, 30, 168, 300]];
    R.forEach(([py, r, nose, tail], i) => {
      const y = S.y(py), x = i === 2 ? 0.035 : 0;
      add(S.tube(nose + r * 2, tail, py, r, 18, x), i === 1 ? cream : body0);
      add(lathe([[S.l(r), S.z(nose + r * 2)], [S.l(r * 0.8), S.z(nose + r)], [S.l(r * 0.4), S.z(nose + r * 0.3)], [S.l(2), S.z(nose)]], 16, x, y), P.enamel(NOSE[i]));
      add(ringZ(S.l(r) + 0.001, 0.003, S.z(nose + r * 2), x, y, 16), P.metal, NO);
      for (const k of [0.55, 1.1]) add(ringZ(S.l(r) * (0.45 + k * 0.38), 0.004, S.z(nose + r * k * 0.95), x, y, 16), P.enamel(i ? C.cream : C.burnt), NO);
    });
    // tape wraps round the bundle
    for (const [x0, x1] of [[322, 352], [380, 408], [410, 440]]) add(S.blk(x0, 248, x1, 522, 0.122, 0.006), P.enamel(C.cream2), { rust: 0, chip: 0.1 });
    for (const sx of [1, -1] as const) {
      add(sideDecal(S.l(50), S.l(50), sx, sx * (S.l(62) + 0.001), S.y(455), S.z(560)), 0xffffff, DEC(DC.warn));
      add(sideDecal(S.l(40), S.l(40), sx, sx * (S.l(55) + 0.001), S.y(300), S.z(480)), 0xffffff, DEC(DC.patch));
    }
  });
  const dish = g.part((add) => {
    const d = lathe([[0.005, 0.0], [0.03, 0.006], [0.055, 0.022], [0.07, 0.04]], 16);
    add(d, P.enamel(C.burnt), { chip: 0.3 });
    add(inward(lathe([[0.004, 0.002], [0.029, 0.008], [0.054, 0.024], [0.068, 0.04]], 16)), shade(P.enamel(C.cream), 0.75), NO);
    add(rod([0, 0, 0.0], [0, 0, 0.06], 0.003, 4), P.steel, NO);
    add(ball(0.007, 0, 0, 0.062, 6, 4), P.enamel(C.orange), { outline: false, glow: C.orangeGlow });
  }, 'none');
  const DY = S.y(150), DZ = S.z(660);
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, S.y(392), S.z(1407)),
    pose(a, s) {
      s.part(body, IDENT, 0);
      if (a.loaded > 0 && a.shotAge > 0.8) s.part(rockets, moved(0, 0, -0.25 * (1 - ramp(a.shotAge, 0.8, 1.3))), 0);
      // the dish faces back over the rockets, tilts and sweeps
      M1.makeRotationX(-0.4);
      M2.makeRotationY(PI + 0.6 * Math.sin(a.t * 2.4));
      s.part(dish, M3.multiplyMatrices(M2, M1).setPosition(0, DY, DZ), 0.6 + 0.6 * Math.max(0, Math.sin(a.t * 8)));
    },
  };
}

// ================================================================== TURRET DEPLOYER
// Traced from turret.png: a cream box with chamfered front corners over a grey lower band, a
// camera eye and a short barrel on the front, an angled lid hinged at the back, the G.U.L.P. seal
// on the flank in corporate ink, legs folded under. Planted two metres ahead on the shot.
export function turretParts(g: GB, P: Paint, onGrip: boolean) {
  const cream = P.enamel(C.cream), grey = P.enamel(C.grey), plum = P.enamel(C.plumDk), OY = onGrip ? 0.11 : 0, OZ = onGrip ? 0.06 : 0;
  const W2 = 0.11, Z0 = OZ - 0.12, Z1 = OZ + 0.12, YB = OY + 0.012, YM = OY + 0.06, YT = OY + 0.165;
  const body = g.part((add) => {
    // cream upper with chamfered front and back corners, the grey band under it
    const top = slab([[Z0 + 0.012, YM], [Z1 - 0.022, YM], [Z1, YM + 0.02], [Z1, YT - 0.012], [Z1 - 0.012, YT], [Z0 + 0.012, YT], [Z0, YT - 0.012], [Z0, YM + 0.01]], 2 * W2, 0, 0.003);
    add(top, cream, { rust: 0.12 });
    add(rb(2 * W2 + 0.006, YM - YB + 0.004, Z1 - Z0 + 0.004, 0.004, 0, (YM + YB) / 2, OZ), grey);
    add(rb(2 * W2 + 0.008, 0.006, Z1 - Z0 + 0.006, 0.002, 0, YM - 0.004, OZ), shade(grey, 0.7), NO);
    for (const z of [OZ - 0.04, OZ + 0.04]) add(box(2 * W2 + 0.002, YT - YM - 0.01, 0.002, 0, (YT + YM) / 2, z), shade(cream, 0.8), NO);
    // front: the dark recessed panel round the eye, the barrel off to the side
    add(rb(0.07, 0.07, 0.012, 0.004, 0.03, OY + 0.11, Z1 + 0.002), P.metal);
    add(cz(0.024, 0.024, Z1, Z1 + 0.02, 16, 0.03, OY + 0.11), P.metal);
    add(ringZ(0.024, 0.004, Z1 + 0.02, 0.03, OY + 0.11, 16), P.brass, NO);
    add(rb(0.04, 0.04, 0.03, 0.004, -0.06, OY + 0.125, Z1 + 0.008), P.metal);
    add(cz(0.012, 0.011, Z1 + 0.02, Z1 + 0.09, 10, -0.06, OY + 0.125), P.steel);
    add(cz(0.016, 0.016, Z1 + 0.08, Z1 + 0.095, 10, -0.06, OY + 0.125), P.metal);
    bore(add, 0.007, Z1 + 0.095, -0.06, OY + 0.125);
    // back: a round port
    add(cz(0.02, 0.02, Z0 - 0.012, Z0, 14, 0.04, OY + 0.13), P.metal);
    // the corporate seal in ink (not red), vents, a label
    for (const sx of [1, -1]) {
      add(ringX(0.03, 0.004, sx * (W2 + 0.0015), OY + 0.11, OZ + 0.05, 20), C.ink, NO);
      add(cx(0.026, 0.002, 18, sx * (W2 + 0.001), OY + 0.11, OZ + 0.05), P.enamel(C.teal), NO);
      for (let k = 0; k < 3; k++) add(ringX(0.008 + k * 0.006, 0.0012, sx * (W2 + 0.0022), OY + 0.11, OZ + 0.05, 14, PI * 1.5), C.ink, NO);
      add(box(0.002, 0.012, 0.03, sx * (W2 + 0.001), OY + 0.145, OZ - 0.04), P.metal, NO);
    }
    add(sideDecal(0.05, 0.03, 1, W2 + 0.003, OY + 0.08, OZ - 0.05), 0xffffff, DEC(DC.plate));
    add(sideDecal(0.06, 0.03, -1, -W2 - 0.003, OY + 0.035, OZ), 0xffffff, DEC(DC.stripes));
  });
  const legs = g.part((add) => {
    for (const sx of [1, -1]) {
      add(rb(0.02, 0.012, Z1 - Z0 - 0.02, 0.003, sx * (W2 - 0.012), YB - 0.008, OZ), plum);
      for (const sz of [1, -1]) add(rb(0.014, 0.018, 0.014, 0.003, sx * (W2 - 0.02), YB - 0.002, OZ + sz * 0.08), P.metal);
    }
  }, 'none');
  const lid = g.part((add) => {
    add(slab([[Z0 - 0.01, 0], [Z1 + 0.005, 0], [Z1 - 0.01, 0.018], [Z0 + 0.03, 0.022]], 2 * W2 + 0.01, 0, 0.002).translate(0, YT + 0.002, 0), shade(cream, 0.94));
    add(cx(0.007, 2 * W2 + 0.004, 8, 0, YT + 0.004, Z0 + 0.004), P.metal, NO);
    add(box(0.06, 0.008, 0.03, 0.04, YT + 0.024, OZ - 0.03), P.metal, NO);
  });
  const eye = g.part((add) => {
    add(cz(0.017, 0.017, Z1 + 0.016, Z1 + 0.024, 14, 0.03, OY + 0.11), C.dark, { glow: 0xff9a4a, outline: false });
  }, 'none');
  return { body, legs, lid, eye, OY, OZ };
}
function turret(P: Paint): GunModel {
  const g = new GB();
  // the deployer: a carry handle on a short post, the trigger in the handle
  const grip = g.part((add) => {
    add(rb(0.05, 0.03, 0.08, 0.006, 0, 0.065, 0.04), P.metal);
    add(rb(0.03, 0.05, 0.04, 0.004, 0, 0.095, 0.05), P.metal);
    add(rb(0.03, 0.04, 0.04, 0.004, 0, 0.035, 0.0), P.metal);
    add(rb(0.12, 0.012, 0.1, 0.003, 0, 0.116, 0.05), P.metal);
    add(rb(0.034, 0.105, 0.05, 0.01, 0, -0.025, -0.01, 0.25), P.enamel(C.plum));
    add(guard(0.016, 0.042, 0.022, 0.0035), P.metal, NO);
    trigger(add, P.metal);
  });
  const t = turretParts(g, P, true);
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, t.OY + 0.11, t.OZ + 0.14),
    pose(a, s) {
      s.part(grip, IDENT, 0);
      if (a.shotAge < 1.2) return; // planted: a fresh one unfolds out of the deployer
      const k = ramp(a.shotAge, 1.2, 1.6);
      M1.makeScale(k, k, k).setPosition(0, t.OY * (1 - k), t.OZ * (1 - k));
      for (const p of [t.body, t.legs, t.lid]) s.part(p, M1, 0);
      s.part(t.eye, M1, 0.6 + 0.4 * Math.sin(a.t * 2.2));
    },
  };
}

// ================================================================== GIANT GROUND FIREWORKS
// Seeker Shells: a tall skyrocket with a bouquet crown of mini-rockets round the nose (they peel
// off as the homing seekers), spindly stake legs, a gold finial, a sputtering fuse. Carried at the
// hip in both arms, lobbed, it plants itself and goes up. The wrapper and nose take the element.
export function fireworkInk(elem: number, P: Paint): { body: number; band: number; nose: number; glow: number } {
  switch (elem) {
    case Elem.Fire: return { body: P.enamel(C.cream), band: P.enamel(C.fire), nose: P.enamel(C.burnt), glow: C.fireGlow };
    case Elem.Ice: return { body: P.enamel(0xdfeef0), band: P.enamel(C.ice), nose: P.enamel(0x6fa6c2), glow: C.iceGlow };
    case Elem.Shock: return { body: P.enamel(C.cream), band: P.enamel(C.shock), nose: P.enamel(C.mustard), glow: C.shockGlow };
    case Elem.Acid: return { body: P.enamel(C.cream2), band: P.enamel(C.acid), nose: P.enamel(0x6a9a2a), glow: C.acidGlow };
    case Elem.Magnetic: return { body: P.enamel(C.cream), band: P.enamel(C.mag), nose: P.enamel(0x5e3a9a), glow: C.magGlow };
    case Elem.Rainbow: return { body: P.enamel(C.ivory), band: P.enamel(C.teal), nose: P.enamel(C.plum), glow: 0xffd27a };
    default: return { body: P.enamel(C.paper), band: P.enamel(C.cream), nose: P.enamel(C.orange), glow: C.orangeGlow };
  }
}
const RAINBOW = [C.fire, C.shock, C.acid, C.ice, C.mag];
/** the seeker skyrocket along +Y, base at y 0 (planted pose). Parts: stand (legs + fuse) and payload. */
export function seekerParts(g: GB, P: Paint, elem: number) {
  const ink = fireworkInk(elem, P);
  const stand = g.part((add) => {
    for (let i = 0; i < 3; i++) {
      const an = (i / 3) * PI * 2 + 0.3;
      add(rod([Math.cos(an) * 0.05, 0.2, Math.sin(an) * 0.05], [Math.cos(an) * 0.2, -0.0, Math.sin(an) * 0.2], 0.007, 5), P.metal);
      add(rod([Math.cos(an) * 0.2, 0.0, Math.sin(an) * 0.2], [Math.cos(an) * 0.21, -0.07, Math.sin(an) * 0.21], 0.004, 4, 0.001), P.steel, NO);
    }
    add(T(new THREE.TorusGeometry(0.06, 0.008, 4, 14), 0, 0.2, 0, PI / 2), P.metal, NO);
  }, 'none');
  const payload = g.part((add) => {
    // after seeker.png: a fat paper egg with a dark lower wrap and the element band, brackets
    // round its waist, a cone and gold finial on top, a dense crown of mini rockets
    const prof: [number, number][] = [];
    for (let i = 0; i <= 32; i++) { const t = i / 32; prof.push([0.145 * Math.pow(Math.sin(0.1 + t * 2.9), 0.55) * (1 - 0.12 * t), 0.12 + t * 0.6]); }
    const lowDk = shade(P.enamel(C.plum), 0.95);
    add(T(new THREE.LatheGeometry(prof.map(([r, y]) => new THREE.Vector2(Math.max(r, 2e-3), y)), 24)), (x, y, z) => {
      if (y < 0.31) return lowDk;
      return Math.sin(Math.atan2(z, x) * 4 + y * 8) > 0.86 ? shade(ink.body, 0.86) : ink.body;
    }, { rust: 0.08 });
    for (let i = 0; i < 4; i++) { const an = (i / 4) * PI * 2 + PI / 4; add(box(0.03, 0.06, 0.05, Math.cos(an) * 0.14, 0.25, Math.sin(an) * 0.14, 0, -an), P.metal); }
    add(ringY(0.14, 0.006, 0, 0.31, 0, 24), P.brass, NO);
    add(T(new THREE.CylinderGeometry(0.1225, 0.1245, 0.04, 24, 1, true), 0, 0.54, 0), ink.band, NO);
    add(T(new THREE.ConeGeometry(0.07, 0.13, 16), 0, 0.79, 0), ink.nose);
    add(T(new THREE.SphereGeometry(0.016, 8, 6), 0, 0.86, 0), P.brass, NO);
    add(T(new THREE.ConeGeometry(0.008, 0.06, 6), 0, 0.9, 0), P.brass, NO);
    // the bouquet crown: two rows of stubby mini rockets splayed round the shoulder
    for (let i = 0; i < 18; i++) {
      const row = i % 2, an = (i / 18) * PI * 2, tilt = row ? 1.15 : 0.75, len = 0.1;
      const r0 = 0.085 + row * 0.02, bx = Math.cos(an) * r0, bz = Math.sin(an) * r0, y0 = 0.68 - row * 0.03;
      const tip = [bx + Math.cos(an) * Math.sin(tilt) * len, y0 + Math.cos(tilt) * len, bz + Math.sin(an) * Math.sin(tilt) * len] as [number, number, number];
      add(rod([bx, y0, bz], tip, 0.013, 6), elem === Elem.Rainbow ? P.enamel(RAINBOW[i % 5]) : i % 3 ? shade(ink.body, 0.75) : ink.band);
      add(T(new THREE.ConeGeometry(0.014, 0.03, 6), tip[0] + Math.cos(an) * Math.sin(tilt) * 0.014, tip[1] + Math.cos(tilt) * 0.014, tip[2] + Math.sin(an) * Math.sin(tilt) * 0.014, Math.sin(an) * tilt, 0, -Math.cos(an) * tilt), ink.nose, NO);
    }
    // fuse out of the base
    add(cy(0.012, 0.03, 8, 0, 0.11, 0), P.metal, NO);
    add(rod([0, 0.1, 0], [0.03, 0.04, 0.02], 0.003, 4), C.cord, NO);
  }, 'round');
  const fuse = g.part((add) => {
    add(ball(0.009, 0.03, 0.04, 0.02, 6, 4), C.orange, { glow: ink.glow === C.orangeGlow ? C.orangeGlow : 0xffb060, outline: false });
  }, 'none');
  return { stand, payload, fuse, top: 0.9 };
}

/** Canopy Shells: a riveted siege mortar on outrigger feet, a giant strapped paper ball on its
 *  barrel. Parts: base (feet, turntable), tube (barrel + gauge, tilts on the trunnion), ball. */
export function canopyParts(g: GB, P: Paint, elem: number, canopy: number) {
  const ink = canopyInk(canopy, P);
  const plum = P.enamel(C.plumDk), grey = P.enamel(C.grey), top = P.enamel(C.burnt);
  void elem;
  // after canopy.png: a riveted box mortar on a heavy pedestal with a gauge, a flat base plate
  // with outrigger feet, the giant strapped paper ball on the front. The box pivots on the
  // pedestal's cheeks (trunnion 0.15 m up) to articulate skyward before it fires.
  const base = g.part((add) => {
    add(rb(0.4, 0.028, 0.42, 0.006, 0, 0.014, 0), plum);
    for (const sx of [1, -1]) for (const sz of [1, -1]) {
      add(rod([sx * 0.14, 0.03, sz * 0.14], [sx * 0.27, 0.004, sz * 0.24], 0.012, 6), P.metal);
      add(rb(0.07, 0.01, 0.07, 0.003, sx * 0.27, 0.004, sz * 0.24), P.metal);
    }
    add(rb(0.15, 0.03, 0.3, 0.004, 0, 0.042, 0), P.metal);
    add(rb(0.14, 0.06, 0.24, 0.006, 0, 0.08, -0.01), grey);
    for (const sx of [1, -1]) {
      add(slab([[-0.09, 0.03], [0.09, 0.03], [0.05, 0.19], [-0.05, 0.19]], 0.03, sx * 0.135, 0.002), grey);
      add(cx(0.034, 0.04, 14, sx * 0.135, 0.15, 0), P.metal);
      rivets(add, P.brass, 4, [sx * 0.151, 0.06, -0.06], [sx * 0.151, 0.06, 0.06], 0.004);
    }
    add(cx(0.03, 0.014, 16, 0.157, 0.1, -0.08), P.brass);
    add(sideDecal(0.05, 0.05, 1, 0.1645, 0.1, -0.08), 0xffffff, DEC(DC.dial));
    add(rod([0.157, 0.12, -0.05], [0.157, 0.16, 0.06], 0.004, 4), P.enamel(C.copper), NO);
  });
  const tube = g.part((add) => {
    // around the trunnion at (0, 0.15, 0): the box points +Z when level, its mouth at z 0.2
    add(rb(0.22, 0.2, 0.4, 0.008, 0, 0.0, 0.0), grey);
    add(rb(0.224, 0.1, 0.4, 0.006, 0, 0.052, 0.0), top);
    add(rb(0.226, 0.204, 0.05, 0.004, 0, 0.0, -0.11), ink.band, NO);
    for (const sx of [1, -1]) {
      for (const y of [0.088, -0.088]) rivets(add, P.brass, 8, [sx * 0.112, y, -0.18], [sx * 0.112, y, 0.18], 0.0035);
      for (const z of [-0.19, 0.03, 0.19]) rivets(add, P.brass, 5, [sx * 0.112, -0.08, z], [sx * 0.112, 0.08, z], 0.0035);
    }
    for (const z of [-0.19, 0.03, 0.19]) rivets(add, P.brass, 5, [-0.09, 0.1015, z], [0.09, 0.1015, z], 0.0035);
    add(rb(0.21, 0.19, 0.012, 0.004, 0, 0.0, -0.205), P.metal);
    add(rb(0.05, 0.03, 0.05, 0.004, -0.06, 0.112, -0.17), P.metal);
    add(rod([-0.06, 0.12, -0.19], [-0.07, 0.15, -0.24], 0.004, 4), C.cord, NO);
    add(rb(0.16, 0.16, 0.012, 0.004, 0, 0.0, 0.2), P.metal);
  });
  const ball_ = g.part((add) => {
    // the paper ball sits in the barrel mouth: centre 0.2 m ahead of the muzzle
    const R = 0.2, bz = 0.2 + R * 0.86;
    add(T(new THREE.SphereGeometry(R, 22, 14), 0, 0, bz), (x, y, z) => {
      const n = Math.sin(x * 47 + y * 13) * Math.sin(z * 31 - y * 23);
      return n > 0.6 ? shade(ink.paper, 0.86) : ink.paper;
    }, { rust: 0.03 });
    for (let i = 0; i < 4; i++) add(T(new THREE.TorusGeometry(R + 0.003, 0.009, 4, 28), 0, 0, bz, 0, (i / 4) * PI, 0), ink.band, NO);
    add(T(new THREE.TorusGeometry(R + 0.004, 0.011, 4, 28), 0, 0, bz, PI / 2), ink.band, NO);
    add(T(new THREE.ConeGeometry(0.03, 0.06, 10), 0, 0, bz + R + 0.02, PI / 2), P.brass, NO);
    add(ringZ(0.085, 0.01, 0.2, 0, 0, 18), P.brass, NO);
  }, 'round');
  const fuse = g.part((add) => {
    add(ball(0.01, -0.07, 0.152, -0.242, 6, 4), C.orange, { glow: 0xffb060, outline: false });
  }, 'none');
  return { base, tube, ball: ball_, fuse, trunnionY: 0.15 };
}
function canopyInk(c: number, P: Paint): { paper: number; band: number } {
  const paper = P.enamel(C.paper);
  switch (c) {
    case Canopy.Fire: return { paper, band: P.enamel(C.fire) };
    case Canopy.Ice: return { paper: P.enamel(0xd9e6e2), band: P.enamel(C.ice) };
    case Canopy.Shock: return { paper, band: P.enamel(C.shock) };
    case Canopy.Acid: return { paper, band: P.enamel(C.acid) };
    case Canopy.Magnetic: return { paper, band: P.enamel(C.mag) };
    case Canopy.Aegis: return { paper: P.enamel(C.cream), band: P.enamel(0x5c86b8) };
    case Canopy.Coolant: return { paper: P.enamel(0xd9e6e2), band: P.enamel(0x6fb8c8) };
    case Canopy.Mending: return { paper: P.enamel(C.cream), band: P.enamel(0x7bb87a) };
    default: return { paper, band: P.enamel(C.orange) };
  }
}

// In the hands the fireworks are carried level at the hip, nose forward: the planted frame (+Y up)
// turns onto +Z, with the grip under the belly.
const CARRY = new THREE.Matrix4().makeRotationX(PI / 2);
function seekerHeld(P: Paint, elem: number): GunModel {
  const g = new GB();
  const p = seekerParts(g, P, elem);
  const place = new THREE.Matrix4().multiplyMatrices(new THREE.Matrix4().makeTranslation(0, 0.13, -0.32), CARRY);
  const legsTucked = new THREE.Matrix4().multiplyMatrices(place, new THREE.Matrix4().makeTranslation(0, 0.05, 0).multiply(new THREE.Matrix4().makeScale(0.45, 1.2, 0.45)));
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, 0.13, 0.58),
    pose(a, s) {
      if (a.shotAge < 1.0) return; // planted and gone up; the next one comes out of the crate
      s.part(p.stand, legsTucked, 0);
      s.part(p.payload, place, 0);
      s.part(p.fuse, place, 0.8 + 0.8 * Math.max(0, Math.sin(a.t * 31)));
    },
  };
}
function canopyHeld(P: Paint, elem: number, canopy: number): GunModel {
  const g = new GB();
  const p = canopyParts(g, P, elem, canopy);
  // base under the arm, the barrel level and the ball out front
  const place = new THREE.Matrix4().makeTranslation(0, -0.03, -0.12);
  const tubeAt = new THREE.Matrix4().makeTranslation(0, -0.03 + p.trunnionY, -0.12);
  return {
    rig: g.rig, muzzle: new THREE.Vector3(0, p.trunnionY - 0.03, 0.47),
    pose(a, s) {
      if (a.shotAge < 1.0) return;
      s.part(p.base, place, 0);
      s.part(p.tube, tubeAt, 0);
      s.part(p.ball, tubeAt, 0);
      s.part(p.fuse, tubeAt, 0.8 + 0.8 * Math.max(0, Math.sin(a.t * 29)));
    },
  };
}

// ================================================================== ordnance pieces
// The same models out of the hand: in flight, planted, spent. Static (rest pose), built once each.
export function ordnanceModel(name: string, wid: number, P: Paint): GunModel {
  const g = new GB(0.008);
  const w = WEAPONS[wid] ?? WEAPONS[W.Rocket];
  const one = (slots: number[], at: THREE.Matrix4 = IDENT, glow = 0): GunModel => ({ rig: g.rig, muzzle: new THREE.Vector3(), pose(_a, s) { for (const k of slots) s.part(k, at, glow); } });
  switch (name) {
    case 'rocket': {
      // the rocket out of the launcher: the nose it showed, a body in the launcher's skin, fins
      const sk = rocketSkin(w.elem, P);
      const k = g.part((add) => {
        add(lathe([[0.012, -0.34], [0.05, -0.32], [0.05, 0.36]], 16), typeof sk.body === 'number' ? sk.body : P.enamel(C.cream));
        add(lathe([[0.05, 0.36], [0.045, 0.44], [0.027, 0.51], [0.005, 0.56]], 16), sk.nose, { glow: sk.noseGlow });
        for (const z of [-0.2, 0.2]) add(ringZ(0.052, 0.005, z, 0, 0, 16), P.brass, NO);
        for (let i = 0; i < 4; i++) { const an = (i / 4) * PI * 2 + PI / 4; add(box(0.005, 0.07, 0.12, Math.cos(an) * 0.075, Math.sin(an) * 0.075, -0.27, 0, 0, an - PI / 2), sk.cone); }
      }, 'round');
      return one([k], IDENT, 0.4);
    }
    case 'badidea': {
      const k = g.part((add) => {
        add(lathe([[0.02, -0.5], [0.075, -0.47], [0.075, 0.42]], 18), P.enamel(C.olive));
        add(lathe([[0.075, 0.42], [0.06, 0.52], [0.03, 0.6], [0.005, 0.65]], 18), P.enamel(C.fire));
        for (const z of [-0.3, 0.0, 0.3]) add(ringZ(0.077, 0.006, z, 0, 0, 18), [C.ice, C.shock, C.acid].map((c) => P.enamel(c))[Math.round((z + 0.3) / 0.3)], NO);
        for (let i = 0; i < 4; i++) { const an = (i / 4) * PI * 2 + PI / 4; add(box(0.006, 0.1, 0.16, Math.cos(an) * 0.11, Math.sin(an) * 0.11, -0.4, 0, 0, an - PI / 2), P.enamel(C.mag)); }
        add(sideDecal(0.08, 0.08, 1, 0.0755, 0, 0.1), 0xffffff, DEC(DC.warn));
        add(sideDecal(0.08, 0.08, -1, -0.0755, 0, -0.1), 0xffffff, DEC(DC.warn));
      }, 'round');
      return one([k], IDENT, 0.3);
    }
    case 'harpoon': {
      const k = g.part((add) => {
        add(cz(0.0065, 0.0065, -0.45, 0.33, 6, 0, 0), P.brass);
        add(lathe([[0.004, 0.32], [0.019, 0.35], [0.013, 0.41], [0.002, 0.46]], 8), P.steel);
        for (let i = 0; i < 3; i++) { const an = (i / 3) * PI * 2; add(rod([Math.cos(an) * 0.01, Math.sin(an) * 0.01, 0.38], [Math.cos(an) * 0.03, Math.sin(an) * 0.03, 0.34], 0.0035, 4, 0.001), P.steel, NO); }
        for (let i = 0; i < 3; i++) { const an = (i / 3) * PI * 2; add(box(0.003, 0.03, 0.06, Math.cos(an) * 0.014, Math.sin(an) * 0.014, -0.42, 0, 0, an - PI / 2), P.enamel(C.seagreen), NO); }
      }, 'none');
      return one([k]);
    }
    case 'beacon': {
      const p = bigOneParts(g, P);
      return { rig: g.rig, muzzle: p.muzzle, pose(a, s) { s.part(p.body, IDENT, 0); s.part(p.lens, IDENT, 1.6); } };
    }
    case 'seekerPlanted': case 'seekerStand': case 'seekerPayload': {
      const p = seekerParts(g, P, w.elem);
      const ks = name === 'seekerPlanted' ? [p.stand, p.payload, p.fuse] : name === 'seekerStand' ? [p.stand, p.fuse] : [p.payload];
      return { rig: g.rig, muzzle: new THREE.Vector3(0, p.top, 0), pose(_a, s) { for (const k of ks) s.part(k, IDENT, k === p.fuse ? 1.5 : 0); } };
    }
    case 'canopyBase': case 'canopyTube': case 'canopyBarrel': case 'canopyBall': {
      const p = canopyParts(g, P, w.elem, w.canopy);
      if (name === 'canopyBase') return one([p.base]);
      if (name === 'canopyBall') return one([p.ball], new THREE.Matrix4().makeTranslation(0, 0, -0.372));
      const ks = name === 'canopyTube' ? [p.tube, p.ball, p.fuse] : [p.tube];
      return { rig: g.rig, muzzle: new THREE.Vector3(), pose(_a, s) { for (const k of ks) s.part(k, IDENT, k === p.fuse ? 1.5 : 0); } };
    }
    case 'turretBody': case 'turretLid': {
      const t = turretParts(g, P, false);
      // the lid's hinge sits at the origin so the deploy can pop it open
      if (name === 'turretLid') return one([t.lid], new THREE.Matrix4().makeTranslation(0, -0.17, 0.115));
      return { rig: g.rig, muzzle: new THREE.Vector3(0, 0.1, 0.2), pose(_a, s) { s.part(t.body, IDENT, 0); s.part(t.eye, IDENT, 1.2); } };
    }
    case 'turretLeg': {
      const k = g.part((add) => {
        add(rb(0.03, 0.03, 0.03, 0.006, 0, 0, 0), P.metal);
        add(rod([0, 0, 0], [0, -0.33, 0.12], 0.012, 6), P.metal);
        add(rb(0.05, 0.016, 0.06, 0.005, 0, -0.335, 0.125), P.enamel(C.plumDk));
      }, 'none');
      return one([k]);
    }
  }
  return one([]);
}

// ================================================================== the table
export function buildGunModel(id: number, P: Paint): GunModel {
  const w = WEAPONS[id] ?? WEAPONS[W.Starter];
  switch (w.id) {
    case W.Starter: return starter(P);
    case W.PulseRifle: return pulseRifle(P);
    case W.AutoMG: return autoMG(P);
    case W.BoltSniper: return boltSniper(P);
    case W.Gentlemen: return gentlemen(P);
    case W.BigIron: return bigIron(P);
    case W.DuelBlaster: return duelBlaster(P);
    case W.Akimbo: return akimbo(P);
    case W.Scoundrel: return scoundrel(P);
    case W.Harpoon: return harpoon(P);
    case W.BigOne: return bigOne(P);
    case W.BadIdea: return badIdea(P);
    case W.Turret: return turret(P);
  }
  if (w.proj === 1) return rocketLauncher(P, w.elem);
  if (w.payload === 2) return canopyHeld(P, w.elem, w.canopy);
  return seekerHeld(P, w.elem);
}
export { bigOneParts };
